import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import { AuthContext } from '../contexts/AuthContext';
import sharepointService from '../services/sharepointService';
import clientAdminService from '../services/clientAdminService';
import bedrockService from '../services/bedrockService';
import promptService from '../services/promptService';
import redactorService from '../services/redactorService';
import FilePreviewModal from '../components/FilePreviewModal';
import EditFileNameModal from '../components/EditFileNameModal';
import AcceptFileModal from '../components/AcceptFileModal';
import SharePointApprovalButton from '../components/SharePointApprovalButton';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import SearchableDropdown from '../components/SearchableDropdown';
import CreateClientModal from '../components/CreateClientModal';
import RecentHistoryDialog from '../components/RecentHistoryDialog';
import BackfillModal from '../components/BackfillModal';
import RelocateFileModal from '../components/RelocateFileModal';
import BulkApprovePushModal from '../components/BulkApprovePushModal';
import RedactorResultsViewer from '../components/RedactorResultsViewer';
import SummaryEditor, { htmlToMarkdown, textToHtml } from '../components/SummaryEditor';
import DataPointsTable, { getExtractionStatus } from '../components/DataPointsTable';
import SummaryViewToggle from '../components/SummaryViewToggle';
import { formatTimestamp } from '../utils/dateUtils';
import { chooseFolderClient } from '../utils/folderClient';
import { validateFilename, hasInvalidFilenameChars } from '../utils/filenameValidation';
import {
  getStatusDisplayName,
  getStatusColorClass,
  canApproveFilename,
  canApproveSummary,
  canPushToSharePoint,
  canReanalyze,
  isProcessingStatus,
  STATUS_QUEUED,
  STATUS_UPLOADED,
  STATUS_ANALYZING,
  STATUS_ANALYZED,
  STATUS_FILENAME_APPROVED,
  STATUS_PUSHING_TO_SHAREPOINT,
  STATUS_PUSHED,
  STATUS_PUSHED_TO_SHAREPOINT_DEPRECATED,
  STATUS_SUMMARY_APPROVED,
  STATUS_DONE,
  STATUS_DELETING,
  STATUS_ANALYSIS_ERROR,
  STATUS_PUSH_ERROR,
  STATUS_DELETE_ERROR
} from '../constants/statusConstants';

// Fallback document type options for dropdown
const DEFAULT_DOC_TYPE_OPTIONS = [
  "Bank",
  "CC",
  "Education",
  "Estate",
  "Employer",
  "Health",
  "Income",
  "Insurance",
  "Investment",
  "Loan",
  "Mixed",
  "Tax",
];

// Fallback sub-type options for dropdown
const DEFAULT_SUB_TYPE_OPTIONS = [
  "1040",
  "401k",
  "403b",
  "457",
  "529",
  "Annuity",
  "Auto",
  "Brokerage",
  "CD",
  "Checking",
  "Credit Card Statement",
  "Deed",
  "ESA",
  "Federal",
  "Home",
  "HSA",
  "IRA",
  "Long Term Care",
  "Medical",
  "Mixed",
  "Money Market",
  "Paystub",
  "Pension",
  "Personal",
  "POA",
  "Roth 401k",
  "Roth IRA",
  "RSU",
  "Savings",
  "SS",
  "State",
  "Student",
  "Tax Return",
  "Treasury Bills",
  "Trust",
  "Umbrella",
  "W-2",
  "Will",
];

// The Summary / Data Points toggle shows when an extraction prompt produced
// data, and also when it rejected the document as not applicable (the Data
// Points view then explains why there is no data).
const hasExtractionResult = (file) => (
  Boolean(file) && ['extracted', 'not_applicable'].includes(
    getExtractionStatus(file.docDataPoints, file.docDataPointsDetails).kind
  )
);

function FileDrop({ onFiles, disabled }) {
  const ref = useRef();
  const inputRef = useRef();

  useEffect(() => {
    const el = ref.current;
    const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };
    const onDrop = (e) => {
      prevent(e);
      if (disabled) {
        alert('Select a client first');
        return;
      }
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length) onFiles(files);
    };

    const onDragOver = (e) => {
      prevent(e);
      if (disabled) return;
    };

    el.addEventListener('dragenter', prevent);
    el.addEventListener('dragover', onDragOver);
    el.addEventListener('drop', onDrop);
    return () => {
      el.removeEventListener('dragenter', prevent);
      el.removeEventListener('dragover', onDragOver);
      el.removeEventListener('drop', onDrop);
    };
  }, [onFiles, disabled]);

  return (
    <div ref={ref} className="border rounded p-4 text-center" style={{ minHeight: 140, cursor: disabled ? 'not-allowed' : 'pointer' }} onClick={() => {
      if (disabled) { alert('Select a client first'); return; }
      if (inputRef.current) inputRef.current.click();
    }}>
      <p className="mb-0">{disabled ? 'Select a client to enable file upload' : 'Drag and drop files here, or click to select.'}</p>
      <input ref={inputRef} type="file" multiple disabled={disabled} onChange={(e) => {
        if (disabled) { alert('Select a client first'); return; }
        onFiles(Array.from(e.target.files));
      }} className="form-control-file mt-2" style={{ display: 'none' }} />
    </div>
  );
}

export default function SharepointUpload() {
  const { hasPermission } = useContext(AuthContext);
  // Use-only access is enough here; editing app-owned prompts stays in Prompt Manager.
  const canUseAppPrompts = hasPermission('prompt_admin')
    || hasPermission('prompt_user')
    || hasPermission('admin');

  const [clients, setClients] = useState([]);
  const [knowledgebaseClients, setKnowledgebaseClients] = useState([]);
  // { [SharePoint folder]: clientId }: the Client Helper client each folder
  // was last mapped to (picked on upload, push or approve).
  const [folderClients, setFolderClients] = useState({});
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedKnowledgebaseClient, setSelectedKnowledgebaseClient] = useState('');
  const [files, setFiles] = useState([]); // {file, s3Key, renamed, newName, status}
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileModalUri, setFileModalUri] = useState(null);
  const [fileModalFilename, setFileModalFilename] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [createModalAutoOpened, setCreateModalAutoOpened] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [creatingClient, setCreatingClient] = useState(false);
  const [weakMatchWarning, setWeakMatchWarning] = useState(null); // { client, sharepointName } when only 1 token matches

  // Summary modal state
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [summaryFilename, setSummaryFilename] = useState(null);
  const [summaryFileKey, setSummaryFileKey] = useState(null);
  const [summaryFileAttributes, setSummaryFileAttributes] = useState(null);
  const [summaryContent, setSummaryContent] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [summaryContentLoading, setSummaryContentLoading] = useState(false);
  const [currentReviewFile, setCurrentReviewFile] = useState(null); // Track the file being reviewed
  const [previewVersion, setPreviewVersion] = useState('redacted');
  const [uploadOriginal, setUploadOriginal] = useState(false);

  // Suggested filename state
  const [suggestedFilename, setSuggestedFilename] = useState(''); // Read-only suggested name from AI
  const [acceptedFilename, setAcceptedFilename] = useState(''); // Editable accepted name
  const [suggestedFilenameLoading, setSuggestedFilenameLoading] = useState(false);
  const [originalAcceptedFilename, setOriginalAcceptedFilename] = useState(''); // Track original accepted filename to detect changes
  const [filenameSaveError, setFilenameSaveError] = useState(''); // Track filename save errors

  // Document summary state
  const [documentSummary, setDocumentSummary] = useState('');
  const [originalDocumentSummary, setOriginalDocumentSummary] = useState(''); // Store original AI-generated summary
  const [editedDocumentSummary, setEditedDocumentSummary] = useState(''); // Store edited summary with annotations
  const [customDocumentSummary, setCustomDocumentSummary] = useState(''); // Store custom summary separately
  const [documentSummaryLoading, setDocumentSummaryLoading] = useState(false);
  const [isCustomSummary, setIsCustomSummary] = useState(false); // Track if current summary is custom
  const [summaryViewMode, setSummaryViewMode] = useState('original'); // 'original', 'edited', or 'custom'
  const [summaryContentView, setSummaryContentView] = useState('summary'); // 'summary' | 'data' (Summary vs Data Points toggle)
  const [triggerSummaryEdit, setTriggerSummaryEdit] = useState(false); // Trigger edit mode in SummaryEditor
  const [summaryViewState, setSummaryViewState] = useState({ isEditing: false, showingOriginal: false }); // Track SummaryEditor view state

  // Custom summary modal state
  const [customSummaryModalOpen, setCustomSummaryModalOpen] = useState(false);
  const [customSummaryPrompt, setCustomSummaryPrompt] = useState('');
  const [wordWrap, setWordWrap] = useState(true);
  const [useTextract, setUseTextract] = useState(false);

  // Client type prompts state for custom summary modal
  const [clientTypePrompts, setClientTypePrompts] = useState([]);
  const [selectedClientPrompt, setSelectedClientPrompt] = useState('');
  const [selectedPromptSystemPrompt, setSelectedPromptSystemPrompt] = useState('');
  const [selectedPromptPrompt, setSelectedPromptPrompt] = useState('');
  const [selectedPromptModelId, setSelectedPromptModelId] = useState('');
  const [promptsLoading, setPromptsLoading] = useState(false);

  // Doc types state for summary modal
  const [docTypesList, setDocTypesList] = useState(DEFAULT_DOC_TYPE_OPTIONS);
  const [docTypesData, setDocTypesData] = useState([]); // Full doc type objects with embedded sub_types
  const [subTypesList, setSubTypesList] = useState(DEFAULT_SUB_TYPE_OPTIONS);

  // Accept file modal state
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [acceptingFile, setAcceptingFile] = useState(null);
  const [isAcceptingFromSummary, setIsAcceptingFromSummary] = useState(false);
  const [isApprovingAll, setIsApprovingAll] = useState(false);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // Files panel filter and sort state
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('original'); // 'original', 'suggested', 'accepted', 'status', 'timestamp'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // Auto-refresh state
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [nextRefreshCountdown, setNextRefreshCountdown] = useState(null);

  // Create SharePoint client modal state
  const [showCreateSharePointClientModal, setShowCreateSharePointClientModal] = useState(false);

  // Status info modal state
  const [showStatusInfoModal, setShowStatusInfoModal] = useState(false);

  // Recent history dialog state
  const [showRecentHistoryDialog, setShowRecentHistoryDialog] = useState(false);

  // Backfill modal state
  const [showBackfillModal, setShowBackfillModal] = useState(false);

  // Relocate file modal state
  const [showRelocateModal, setShowRelocateModal] = useState(false);
  const [inlineRedactionJobData, setInlineRedactionJobData] = useState(null);
  const [inlineRedactionLoading, setInlineRedactionLoading] = useState(false);
  const [inlineRedactionError, setInlineRedactionError] = useState('');

  /**
   * Get doc_type name from doc_type ID.
   * Backend stores doc_type_id (UUID), but UI displays names.
   */
  const getDocTypeName = (docTypeId) => {
    if (!docTypeId || !docTypesData || docTypesData.length === 0) return '';
    const docTypeObj = docTypesData.find(dt => dt.id === docTypeId);
    return docTypeObj?.name || '';
  };

  /**
   * Get doc_type ID from doc_type name.
   */
  const getDocTypeId = (docTypeName) => {
    if (!docTypeName || !docTypesData || docTypesData.length === 0) return '';
    const docTypeObj = docTypesData.find(dt => dt.name === docTypeName);
    return docTypeObj?.id || '';
  };

  /**
   * Get sub_type name from sub_type ID.
   * Backend stores sub_type_id (UUID), but UI displays names.
   */
  const getSubTypeName = (subTypeId) => {
    if (!subTypeId || !docTypesData || docTypesData.length === 0) return '';
    for (const dt of docTypesData) {
      if (dt.sub_types && dt.sub_types.length > 0) {
        const subType = dt.sub_types.find(st => (typeof st === 'object' ? st.id : null) === subTypeId);
        if (subType) return typeof subType === 'object' ? subType.name : subType;
      }
    }
    return '';
  };

  /**
   * Get sub_type ID from sub_type name within a specific doc_type.
   */
  const getSubTypeId = (subTypeName, docTypeId) => {
    if (!subTypeName || !docTypesData || docTypesData.length === 0) return '';
    const docTypeObj = docTypesData.find(dt => dt.id === docTypeId);
    if (docTypeObj && docTypeObj.sub_types) {
      const subType = docTypeObj.sub_types.find(st => (typeof st === 'object' ? st.name : st) === subTypeName);
      return subType && typeof subType === 'object' ? subType.id : '';
    }
    return '';
  };

  /**
   * Get the 'Mixed' doc_type ID for fallback.
   */
  const getMixedDocTypeId = () => {
    return getDocTypeId('Mixed') || 'Mixed';
  };

  /**
   * Get the 'Mixed' sub_type ID for fallback within the Mixed doc_type.
   */
  const getMixedSubTypeId = () => {
    const mixedDocTypeId = getDocTypeId('Mixed');
    return getSubTypeId('Mixed', mixedDocTypeId) || 'Mixed';
  };

  /**
   * Get filtered sub_types based on selected doc_type ID.
   * Returns sub_types from the matching doc_type object, or all sub_types if no doc_type selected.
   */
  const getFilteredSubTypes = (selectedDocTypeId) => {
    if (!selectedDocTypeId || !docTypesData || docTypesData.length === 0) {
      // No doc_type selected, return all sub_types
      return subTypesList;
    }
    // Find the doc_type object that matches the selected ID
    const docTypeObj = docTypesData.find(dt => dt.id === selectedDocTypeId);
    if (docTypeObj && docTypeObj.sub_types && docTypeObj.sub_types.length > 0) {
      // Extract sub_type names from the doc_type's sub_types array
      return docTypeObj.sub_types.map(st => typeof st === 'object' ? st.name : st).filter(Boolean);
    }
    // Doc type found but has no sub_types, return empty array
    return [];
  };

  useEffect(() => {
    sharepointService.getClients().then(setClients);
    sharepointService.getClientFolderMap().then(setFolderClients);

    // Fetch knowledgebase clients
    const fetchKnowledgebaseClients = async () => {
      try {
        const kbClients = await clientAdminService.fetchAllClients();
        setKnowledgebaseClients(kbClients);
      } catch (error) {
        console.error('Error fetching knowledgebase clients:', error);
        setKnowledgebaseClients([]);
      }
    };
    fetchKnowledgebaseClients();

    // Load doc_types for summary modal
    const loadDocTypes = async () => {
      try {
        const docTypesResp = await bedrockService.getDocTypes();
        const types = docTypesResp?.doc_types || [];
        // Store full doc type objects for sub_type filtering
        setDocTypesData(types);
        // Extract name property from objects, or use string directly for backward compatibility
        const typeNames = types.map(t => typeof t === 'object' ? t.name : t).filter(Boolean);
        setDocTypesList(typeNames.length > 0 ? typeNames : DEFAULT_DOC_TYPE_OPTIONS);

        // Extract all sub_types from all doc_types for the global subTypesList
        const allSubTypes = new Set();
        types.forEach(dt => {
          if (dt.sub_types && Array.isArray(dt.sub_types)) {
            dt.sub_types.forEach(st => {
              const name = typeof st === 'object' ? st.name : st;
              if (name) allSubTypes.add(name);
            });
          }
        });
        setSubTypesList(allSubTypes.size > 0 ? Array.from(allSubTypes).sort() : DEFAULT_SUB_TYPE_OPTIONS);
      } catch (error) {
        console.error('Error loading doc types:', error);
      }
    };
    loadDocTypes();

    // Load client type prompts for custom summary modal
    const loadClientTypePrompts = async () => {
      try {
        setPromptsLoading(true);
        const response = await promptService.getPrompts();

        if (response.success && response.prompts) {
          // Filter for client type prompts (owner_type is 'client')
          // Users allowed to use app-owned prompts see the unfiltered list
          const clientPrompts = canUseAppPrompts
            ? response.prompts
            : response.prompts.filter(prompt =>
              prompt.owner_type === 'client'
            );

          setClientTypePrompts(clientPrompts);
          console.log('Loaded client type prompts:', clientPrompts);
        } else {
          console.warn('Failed to load prompts:', response);
          setClientTypePrompts([]);
        }
      } catch (error) {
        console.error('Error loading client type prompts:', error);
        setClientTypePrompts([]);
      } finally {
        setPromptsLoading(false);
      }
    };
    loadClientTypePrompts();
  }, []); // Empty dependency array ensures this runs only once on mount

  useEffect(() => {
    if (selectedClient) {
      loadFiles();
    } else {
      setFiles([]);
    }
  }, [selectedClient]);

  useEffect(() => {
    setSelectedFileIds((prev) => {
      if (!prev.length) return prev;
      const validIds = new Set(files.map((f) => f.fileId).filter(Boolean));
      return prev.filter((id) => validIds.has(id));
    });
  }, [files]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefreshEnabled && selectedClient) {
      const interval = setInterval(() => {
        // Only auto-refresh if the page is visible (user is on this tab)
        if (!document.hidden) {
          loadFiles();
        }
      }, 60000); // 60 seconds = 1 minute

      setRefreshInterval(interval);

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    } else {
      // Clear interval if auto-refresh is disabled
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
  }, [autoRefreshEnabled, selectedClient]);

  // Countdown effect for next refresh
  useEffect(() => {
    let countdownInterval;

    if (autoRefreshEnabled && lastRefreshTime) {
      countdownInterval = setInterval(() => {
        const now = new Date();
        const nextRefresh = new Date(lastRefreshTime.getTime() + 60000); // 1 minute after last refresh
        const secondsUntilRefresh = Math.max(0, Math.floor((nextRefresh - now) / 1000));

        if (secondsUntilRefresh > 0) {
          setNextRefreshCountdown(secondsUntilRefresh);
        } else {
          setNextRefreshCountdown(null);
        }
      }, 1000);
    } else {
      setNextRefreshCountdown(null);
    }

    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [autoRefreshEnabled, lastRefreshTime]);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [refreshInterval]);

  // Function to find matching knowledgebase client.
  // Returns { client, score } where score is the number of matching tokens,
  // or Infinity for an exact match, or null if no tokens match.
  const findMatchingKnowledgebaseClient = (sharepointClientName) => {
    if (!sharepointClientName || !knowledgebaseClients.length) return null;

    const normalized = sharepointClientName
      .replace(/_REBELLIONAIRE/gi, '')
      .toLowerCase()
      .trim();

    // Exact name match
    const exactMatch = knowledgebaseClients.find(c =>
      c.client_name.toLowerCase().trim() === normalized
    );
    if (exactMatch) return { client: exactMatch, score: Infinity };

    // Tokenize on whitespace and underscores; skip single-char tokens to reduce noise
    const spTokens = normalized.split(/[\s_]+/).filter(t => t.length > 1);
    const spSet = new Set(spTokens);

    let bestScore = 0;
    let bestClient = null;

    for (const kbClient of knowledgebaseClients) {
      // Score against client name
      const kbTokens = kbClient.client_name.toLowerCase().trim().split(/[\s_]+/).filter(t => t.length > 1);
      const score = kbTokens.filter(t => spSet.has(t)).length;
      if (score > bestScore) {
        bestScore = score;
        bestClient = kbClient;
      }

      // Score against nicknames
      if (kbClient.nicknames) {
        for (const nickname of kbClient.nicknames) {
          const nickNorm = nickname.toLowerCase().trim();
          if (nickNorm === normalized) return { client: kbClient, score: Infinity }; // Exact nickname match
          const nickTokens = nickNorm.split(/[\s_]+/).filter(t => t.length > 1);
          const nickScore = nickTokens.filter(t => spSet.has(t)).length;
          if (nickScore > bestScore) {
            bestScore = nickScore;
            bestClient = kbClient;
          }
        }
      }
    }

    if (bestScore === 0) return null;
    return { client: bestClient, score: bestScore };
  };

  // Function to handle SharePoint client selection
  const handleSharepointClientChange = (clientName) => {
    setSelectedClient(clientName);

    // Reset auto-refresh when changing clients
    setAutoRefreshEnabled(false);
    setLastRefreshTime(null);
    setNextRefreshCountdown(null);

    // The folder's own client (picked for it before) wins; otherwise guess
    // from the name. With no client, the previous folder's is cleared so an
    // upload can't carry it (and map this folder to it).
    const choice = chooseFolderClient(clientName, folderClients, knowledgebaseClients, findMatchingKnowledgebaseClient);
    setSelectedKnowledgebaseClient(choice.clientId);
    setWeakMatchWarning(choice.source === 'weak' ? { client: choice.client, sharepointName: clientName } : null);
    if (choice.source === 'none') {
      // No match — prompt to create
      setNewClientName(clientName.replace(/_REBELLIONAIRE/gi, '').trim());
      setCreateModalAutoOpened(true);
      setShowCreateClientModal(true);
    }
  };

  // Function to create new knowledgebase client
  const handleCreateKnowledgebaseClient = async () => {
    if (!newClientName.trim()) return;

    try {
      setCreatingClient(true);
      const newClient = await clientAdminService.createClient({
        client_name: newClientName.trim(),
        description: `Auto-created from SharePoint client: ${selectedClient}`,
        nicknames: selectedClient ? [selectedClient] : []
      });

      // Immediately add the new client to the list (avoids DynamoDB eventual-consistency lag)
      setKnowledgebaseClients(prev => [...prev, newClient]);

      // Select the newly created client
      setSelectedKnowledgebaseClient(newClient.client_id);
      setWeakMatchWarning(null);

      setShowCreateClientModal(false);
      setNewClientName('');

      // Refresh in the background to ensure list is up-to-date
      clientAdminService.fetchAllClients()
        .then(kbClients => setKnowledgebaseClients(kbClients))
        .catch(err => console.error('Background refresh of KB clients failed:', err));
    } catch (error) {
      console.error('Error creating knowledgebase client:', error);
      alert(`Failed to create knowledgebase client: ${error.response?.data?.detail || error.message}`);
    } finally {
      setCreatingClient(false);
    }
  };

  // Function to load SharePoint clients
  const loadSharepointClients = async () => {
    try {
      const clients = await sharepointService.getClients();
      setClients(clients);
    } catch (error) {
      console.error('Error loading SharePoint clients:', error);
    }
  };

  // Function to handle SharePoint client creation
  const handleSharePointClientCreated = async () => {
    try {
      // Reload SharePoint clients
      await loadSharepointClients();
      setShowCreateSharePointClientModal(false);
    } catch (error) {
      console.error('Error refreshing SharePoint clients after creation:', error);
    }
  };

  async function loadFiles() {
    if (!selectedClient) return;
    try {
      const list = await sharepointService.listUploads(selectedClient);
      // Convert backend format to frontend format
      setFiles(list.map((l) => ({
        file: { name: l.originalFilename || l.originalName || 'Unknown' },
        fileId: l.fileId, // Add fileId from backend
        s3Key: l.s3Key,
        suggestedName: l.suggestedName,
        acceptedName: l.acceptedName, // Store accepted name separately
        newName: l.acceptedName || l.suggestedName, // For backward compatibility
        status: l.status || 'uploaded',
        url: l.url,
        uploadedBy: l.uploadedBy,
        timestamp: l.timestamp,
        documentSummary: l.docSummary || '', // Add stored document summary
        editedDocumentSummary: l.editedDocumentSummary || '', // Add edited document summary
        suggestedFolder: l.suggestedFolder || '', // AI-suggested folder
        folderConfidence: l.folderConfidence || '', // AI confidence level
        folderReasoning: l.folderReasoning || '', // AI reasoning
        sharepointLocation: l.sharepointLocation || '', // SharePoint file path for relocate feature
        docType: l.docType || '', // Document type classification
        subType: l.subType || '', // Document sub-type
        accountNumber: l.accountNumber || '', // Account number
        redactionEnabled: !!(l.redactionEnabled || l.redactionJobId),
        redactionCategories: l.redactionCategories || [],
        redactionJobId: l.redactionJobId || '',
        redactionFileId: l.redactionFileId || '',
        redactionStatus: l.redactionStatus || '',
        redactionError: l.redactionError || '',
      })));
      setLastRefreshTime(new Date());
    } catch (error) {
      console.error('Error loading files:', error);
      setFiles([]);
    }
  }

  function addFiles(list) {
    const newItems = list.map((f) => ({ file: f, status: 'queued' }));
    setFiles((prev) => [...prev, ...newItems]);
    // Auto-upload each newly added file
    newItems.forEach((item) => autoUpload(item));
  }

  function isBulkEligible(file) {
    return !!file?.fileId && (canApproveFilename(file.status) || canPushToSharePoint(file.status));
  }

  function isRedactionBlocking(file) {
    const status = file?.redactionStatus || 'pending';
    return !!file?.redactionJobId && ['pending', 'processing'].includes(status);
  }

  function toggleFileSelection(fileId) {
    setSelectedFileIds((prev) => {
      if (prev.includes(fileId)) {
        return prev.filter((id) => id !== fileId);
      }
      return [...prev, fileId];
    });
  }

  function toggleSelectAllVisible(filesToToggle, checked) {
    const ids = filesToToggle.map((f) => f.fileId).filter(Boolean);
    if (!ids.length) return;
    setSelectedFileIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, ...ids]));
      }
      return prev.filter((id) => !ids.includes(id));
    });
  }

  function getClientBasePath(clientName) {
    if (!clientName) return null;
    const firstChar = clientName.trim().toUpperCase().charAt(0);
    let letterRange = '';
    if (firstChar >= 'A' && firstChar <= 'F') {
      letterRange = 'Clients A - F';
    } else if (firstChar >= 'G' && firstChar <= 'K') {
      letterRange = 'Clients G - K';
    } else if (firstChar >= 'L' && firstChar <= 'Q') {
      letterRange = 'Clients L - Q';
    } else if (firstChar >= 'R' && firstChar <= 'Z') {
      letterRange = 'Clients R - Z';
    } else {
      return null;
    }
    return `Client Documents/${letterRange}/${clientName.trim()}`;
  }

  function normalizeFolderPath(value) {
    return (value || '').replace(/\\/g, '/').replace(/^\/+|\/+$/g, '').trim();
  }

  function resolveBulkFolderPath(file, folders, basePath) {
    const normalizedBasePath = normalizeFolderPath(basePath || '');
    const normalizedSuggestion = normalizeFolderPath(file?.suggestedFolder || 'FP Docs/Notes & Info');

    if (normalizedSuggestion.toLowerCase().startsWith('client documents/')) {
      return normalizedSuggestion;
    }

    const allFolders = Array.isArray(folders) ? folders : [];
    const folderMatch = allFolders.find((folder) => {
      const fullPath = normalizeFolderPath(folder.full_path || folder.path || '');
      const relativePath = normalizedBasePath && fullPath.toLowerCase().startsWith(`${normalizedBasePath.toLowerCase()}/`)
        ? fullPath.substring(normalizedBasePath.length + 1)
        : fullPath;
      const folderName = normalizeFolderPath(folder.name || '');

      return (
        fullPath.toLowerCase() === `${normalizedBasePath}/${normalizedSuggestion}`.toLowerCase() ||
        fullPath.toLowerCase().endsWith(`/${normalizedSuggestion}`.toLowerCase()) ||
        relativePath.toLowerCase() === normalizedSuggestion.toLowerCase() ||
        folderName.toLowerCase() === normalizedSuggestion.toLowerCase().split('/').pop()
      );
    });

    if (folderMatch) {
      return normalizeFolderPath(folderMatch.full_path || folderMatch.path || '');
    }

    if (normalizedBasePath) {
      return `${normalizedBasePath}/${normalizedSuggestion}`;
    }

    return normalizedSuggestion;
  }

  async function handleBulkApproveSubmit(rows) {
    if (!selectedClient || !selectedKnowledgebaseClient) {
      alert('Select both SharePoint and Knowledgebase clients before running bulk approve.');
      return;
    }

    if (!rows || rows.length === 0) {
      alert('Select at least one file in the bulk modal.');
      return;
    }

    setBulkSubmitting(true);

    const failures = [];
    let successCount = 0;

    try {
      const basePath = getClientBasePath(selectedClient);
      let folders = [];

      try {
        const foldersResp = await sharepointService.getClientFolders(selectedClient, basePath);
        folders = foldersResp?.folders || [];
      } catch (folderError) {
        console.warn('Could not load folder map for bulk approve, using fallback paths:', folderError);
      }

      for (const row of rows) {
        try {
          const matchedFile = files.find((f) => f.fileId === row.fileId);
          if (!matchedFile) {
            throw new Error('File not found in current list');
          }

const acceptedName = (row.suggestedName || '').trim();
if (!acceptedName) {
  throw new Error('Suggested filename is required');
}
if (hasInvalidFilenameChars(acceptedName)) {
  throw new Error('Filename cannot contain brackets ([ or ]) or slashes (/ or \\).');
}

          await sharepointService.updateFileName(row.fileId, acceptedName, selectedClient);

          const docType = row.docType || matchedFile.docType || getMixedDocTypeId();
          const subType = row.subType || matchedFile.subType || getMixedSubTypeId();
          const accountNumber = (row.accountNumber || matchedFile.accountNumber || 'NONE').trim() || 'NONE';

          await sharepointService.saveFileMetadata(
            row.fileId,
            selectedClient,
            undefined,
            docType,
            subType,
            accountNumber
          );

          const folderPath = resolveBulkFolderPath(
            { suggestedFolder: row.suggestedFolder || matchedFile.suggestedFolder },
            folders,
            basePath
          );

          const approveResp = await sharepointService.approveFileAndSummary(
            row.fileId,
            selectedClient,
            selectedKnowledgebaseClient,
            'bulk',
            folderPath,
            false,
            docType,
            subType,
            accountNumber
          );

          if (!approveResp?.success) {
            throw new Error(approveResp?.message || approveResp?.error || 'Approve/push failed');
          }

          successCount += 1;
        } catch (rowError) {
          failures.push({ row, error: rowError.message || 'Unknown error' });
        }
      }

      await loadFiles();

      if (successCount > 0) {
        const succeededIds = rows
          .filter((r) => !failures.some((f) => f.row.fileId === r.fileId))
          .map((r) => r.fileId);
        setSelectedFileIds((prev) => prev.filter((id) => !succeededIds.includes(id)));
      }

      if (failures.length === 0) {
        setShowBulkApproveModal(false);
        alert(`Successfully approved and pushed ${successCount} file(s).`);
      } else {
        const failedPreview = failures
          .slice(0, 5)
          .map((f) => `${f.row.originalName || f.row.fileId}: ${f.error}`)
          .join('\n');
        alert(
          `Bulk approve completed with partial failures.\n\n` +
          `Succeeded: ${successCount}\n` +
          `Failed: ${failures.length}\n\n` +
          failedPreview +
          (failures.length > 5 ? '\n...' : '')
        );
      }
    } finally {
      setBulkSubmitting(false);
    }
  }

  async function autoUpload(item) {
    if (!selectedClient) return;
    try {
      setFiles((prev) => prev.map((x) => x === item ? { ...x, status: 'uploading' } : x));
      const res = await sharepointService.uploadFile(item.file, selectedClient, selectedKnowledgebaseClient);
      const renamed = await sharepointService.renameFile(res.s3Key);
      setFiles((prev) => prev.map((x) => x === item ? {
        ...x,
        fileId: res.fileId, // Add fileId from upload response
        s3Key: res.s3Key,
        url: res.url,
        status: 'renamed',
        suggestedName: renamed.newName, // Set as suggested name for now
        acceptedName: renamed.newName,  // Also set as accepted name initially
        newName: renamed.newName        // Keep for backward compatibility
      } : x));

      // Refresh the files list from the backend to get the complete list
      await loadFiles();
    } catch (e) {
      console.error('Upload error:', e);
      setFiles((prev) => prev.map((x) => x === item ? { ...x, status: 'error' } : x));
    }
  }

  async function handleAnalyze(item) {
    setFiles((prev) => prev.map((f) => f === item ? { ...f, status: 'analyzing' } : f));
    try {
      // Start async workflow
      const res = await sharepointService.analyzeFilesByIds([item.fileId], selectedClient);
      if (res.success && res.results && res.results.length > 0) {
        const jobId = res.results[0].jobId; // job_id is in jobId field

        if (jobId) {
          // Poll for workflow completion
          const pollInterval = 2000; // Poll every 2 seconds
          const maxAttempts = 150; // Max 5 minutes (150 * 2 seconds)

          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise(resolve => setTimeout(resolve, pollInterval));

            try {
              const statusResp = await sharepointService.getWorkflowStatus(jobId);

              // Update UI with current status
              const workflowStatus = statusResp.status;
              if (workflowStatus === 'analyzing_filename') {
                setFiles((prev) => prev.map((f) => f === item ? { ...f, status: 'analyzing', statusDetail: 'Analyzing filename...' } : f));
              } else if (workflowStatus === 'filename_generated') {
                setFiles((prev) => prev.map((f) => f === item ? { ...f, status: 'analyzing', statusDetail: 'Filename generated, determining category...' } : f));
              } else if (workflowStatus === 'determining_category') {
                setFiles((prev) => prev.map((f) => f === item ? { ...f, status: 'analyzing', statusDetail: 'Determining category...' } : f));
              } else if (workflowStatus === 'summarizing') {
                setFiles((prev) => prev.map((f) => f === item ? { ...f, status: 'analyzing', statusDetail: 'Generating summary...' } : f));
              } else if (workflowStatus === 'suggesting_folder') {
                setFiles((prev) => prev.map((f) => f === item ? { ...f, status: 'analyzing', statusDetail: 'Suggesting folder...' } : f));
              } else if (workflowStatus === 'completed') {
                // Workflow completed successfully
                console.log('Analysis workflow completed:', statusResp);

                // Update file with new data from workflow
                setFiles((prev) => prev.map((f) => f === item ? {
                  ...f,
                  status: 'analyzed',
                  statusDetail: null,
                  suggestedName: statusResp.suggested_filename || f.suggestedName,
                  category: statusResp.category || f.category,
                  documentSummary: statusResp.summary || f.documentSummary,
                  suggestedFolder: statusResp.suggested_folder || f.suggestedFolder,
                  folderConfidence: statusResp.folder_confidence || f.folderConfidence,
                  folderReasoning: statusResp.folder_reasoning || f.folderReasoning
                } : f));

                // Also refresh from backend to ensure consistency
                await loadFiles();
                break;
              } else if (workflowStatus === 'failed') {
                console.error('Analysis workflow failed:', statusResp.error);
                setFiles((prev) => prev.map((f) => f === item ? { ...f, status: 'analysis_error', statusDetail: statusResp.error } : f));
                break;
              }
            } catch (pollError) {
              console.error('Error polling workflow status:', pollError);
              // Continue polling on error
            }
          }
        } else {
          console.warn('No job ID returned from analysis');
          setFiles((prev) => prev.map((f) => f === item ? { ...f, status: 'analysis_error' } : f));
        }
      } else {
        setFiles((prev) => prev.map((f) => f === item ? { ...f, status: 'analysis_error' } : f));
      }
    } catch (error) {
      console.error('Analysis error:', error);
      setFiles((prev) => prev.map((f) => f === item ? { ...f, status: 'analysis_error' } : f));
    }
  }

  async function handleDelete(item) {
    if (!item.fileId || !selectedClient) {
      console.error('Cannot delete file: missing fileId or selectedClient');
      alert('Cannot delete file: missing required information');
      return;
    }

    // Show delete confirmation dialog
    setFileToDelete(item);
    setDeleteDialogOpen(true);
  }

  // Handle the actual deletion after user confirms
  async function confirmDelete() {
    if (!fileToDelete) return;

    setFiles((prev) => prev.map((f) => f === fileToDelete ? { ...f, status: 'deleting' } : f));

    try {
      console.log(`Deleting file ${fileToDelete.fileId} for client ${selectedClient}`);

      const result = await sharepointService.deleteFile(fileToDelete.fileId, selectedClient);

      if (result.success) {
        console.log('File deleted successfully:', result);

        // Show success message with details
        const fileName = fileToDelete.acceptedName || fileToDelete.suggestedName || fileToDelete.file?.name || 'Unknown file';
        let message = `File "${fileName}" deleted successfully.`;
        if (result.details) {
          const deletedLocations = [];
          if (result.details.s3_deleted) deletedLocations.push('S3 storage');
          if (result.details.sharepoint_deleted) deletedLocations.push('SharePoint');
          if (result.details.dynamodb_deleted) deletedLocations.push('metadata');

          if (deletedLocations.length > 0) {
            message += `\n\nRemoved from: ${deletedLocations.join(', ')}`;
          }
        }

        // For non-critical notifications, you might want to use a toast instead of alert
        // For now, using console.log for success details
        console.log(message);

        // Refresh the files list to remove the deleted file
        await loadFiles();
      } else {
        console.warn('File deletion reported as unsuccessful:', result);
        setFiles((prev) => prev.map((f) => f === fileToDelete ? { ...f, status: 'error' } : f));
        alert(`Failed to delete file: ${result.message || result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      setFiles((prev) => prev.map((f) => f === fileToDelete ? { ...f, status: 'error' } : f));

      // Show user-friendly error message
      const errorMessage = error.message || 'An unexpected error occurred while deleting the file';
      alert(`Error deleting file: ${errorMessage}`);
    } finally {
      // Close the dialog and clear the file to delete
      setDeleteDialogOpen(false);
      setFileToDelete(null);
    }
  }

  // Handle canceling the delete operation
  function cancelDelete() {
    setDeleteDialogOpen(false);
    setFileToDelete(null);
  }

  const handleAcceptConfirm = async (acceptResult) => {
    if (acceptResult.success) {
      // Determine the new status based on whether this was a combined approval
      const newStatus = isApprovingAll ? STATUS_DONE : STATUS_PUSHED;

      // Update the file status in the local state
      setFiles(prev => prev.map(f =>
        f === acceptingFile ? {
          ...f,
          status: newStatus,
          sharepointLocation: acceptResult.sharepoint_location
        } : f
      ));

      // Refresh the files list to get updated data from backend
      await loadFiles();

      // If accepting from summary modal, close it
      if (isAcceptingFromSummary) {
        closeSummaryModal();
      }
    }
  };

  const closeAcceptModal = () => {
    setUploadOriginal(false);
    setAcceptModalOpen(false);
    setAcceptingFile(null);
    setIsAcceptingFromSummary(false);
    setIsApprovingAll(false);
  };

  // Approve summary only
  const handleApproveSummary = async () => {
    if (!currentReviewFile) return;

    try {
      const response = await sharepointService.approveSummary(
        currentReviewFile.fileId,
        selectedClient
      );

      if (response.success) {
        // Update local state
        setFiles(prev => prev.map(f =>
          f.fileId === currentReviewFile.fileId ? { ...f, status: STATUS_DONE } : f
        ));
        closeSummaryModal();
      } else {
        alert(`Failed to approve summary: ${response.error}`);
      }
    } catch (error) {
      console.error('Error approving summary:', error);
      alert('Error approving summary');
    }
  };

  // Check if a file can be relocated (renamed/moved after being pushed to SharePoint)
  const canRelocateFile = (file) => {
    if (!file) return false;
    // File must be in SharePoint (have a sharepointLocation) and have a fileId
    const hasSharepointLocation = !!file.sharepointLocation;
    const hasFileId = !!file.fileId;
    // File must be in a post-push status (pushed, summary_approved, or done)
    const isInSharepoint = file.status === STATUS_PUSHED ||
      file.status === STATUS_PUSHED_TO_SHAREPOINT_DEPRECATED ||
      file.status === STATUS_SUMMARY_APPROVED ||
      file.status === STATUS_DONE;
    return hasSharepointLocation && hasFileId && isInSharepoint;
  };

  // Handle opening the relocate modal
  const handleOpenRelocateModal = () => {
    if (currentReviewFile && canRelocateFile(currentReviewFile)) {
      setShowRelocateModal(true);
    }
  };

  // Handle successful file relocation
  const handleFileRelocated = async (result) => {
    if (result.success) {
      // Update local state with new location
      setFiles(prev => prev.map(f =>
        f.fileId === result.file?.fileId ? {
          ...f,
          sharepointLocation: result.newPath,
          acceptedName: result.newFilename,
          newName: result.newFilename
        } : f
      ));

      // Update current review file if still open
      if (currentReviewFile && currentReviewFile.fileId === result.file?.fileId) {
        setCurrentReviewFile(prev => prev ? ({
          ...prev,
          sharepointLocation: result.newPath,
          acceptedName: result.newFilename,
          newName: result.newFilename
        }) : null);
        setAcceptedFilename(result.newFilename);
        setOriginalAcceptedFilename(result.newFilename);
      }

      // Refresh files list to get updated data from backend
      await loadFiles();

      // Show success message
      alert(`File successfully relocated to: ${result.newPath}`);
    }
  };

  // Combined: Approve filename, push to SharePoint, and approve summary
  const handleApproveAllFromModal = async (version = previewVersion) => {
    if (!currentReviewFile || !selectedKnowledgebaseClient) {
      alert('Please select a knowledgebase client first.');
      return;
    }

    // Save filename if changed
    if (currentReviewFile.fileId &&
      acceptedFilename && acceptedFilename.trim() &&
      acceptedFilename !== originalAcceptedFilename) {
      try {
        await saveAcceptedFilename(currentReviewFile.fileId, acceptedFilename.trim());

        // Update current review file with new name
        setCurrentReviewFile(prev => prev ? ({
          ...prev,
          acceptedName: acceptedFilename.trim(),
          newName: acceptedFilename.trim()
        }) : null);

        // Update files array
        setFiles(prev => prev.map(f =>
          f.fileId === currentReviewFile.fileId ? { ...f, acceptedName: acceptedFilename.trim(), newName: acceptedFilename.trim() } : f
        ));
      } catch (error) {
        console.error('Failed to save accepted filename:', error);
        alert('Error saving filename. Please try again.');
        return;
      }
    }

    // Save metadata
    if (currentReviewFile.fileId && summaryFileAttributes && selectedClient) {
      try {
        const docType = summaryFileAttributes.doc_type || 'Mixed';
        const subType = summaryFileAttributes.sub_type || 'Mixed';
        const accountNumber = summaryFileAttributes.account_number || 'NONE';

        const metadataSaveResult = await sharepointService.saveFileMetadata(
          currentReviewFile.fileId,
          selectedClient,
          undefined, // editedSummary
          docType,
          subType,
          accountNumber
        );

        if (!metadataSaveResult.success) {
          console.warn('Failed to save metadata:', metadataSaveResult.error);
          if (!window.confirm('Failed to save metadata. Continue anyway?')) {
            return;
          }
        }
      } catch (error) {
        console.error('Error saving metadata:', error);
        if (!window.confirm('Error saving metadata. Continue anyway?')) {
          return;
        }
      }
    }

    // Show accept modal with combined approval mode
    setUploadOriginal(version === 'original');
    setAcceptingFile(currentReviewFile);
    setIsAcceptingFromSummary(true);
    setIsApprovingAll(true);
    setAcceptModalOpen(true);
  };

  // Function to handle accept from summary modal (push to SharePoint only)
  const handleAcceptFromSummary = async (version = previewVersion) => {
    if (currentReviewFile) {
      // Save filename if it has changed before proceeding with accept
      if (currentReviewFile.fileId &&
        acceptedFilename && acceptedFilename.trim() &&
        acceptedFilename !== originalAcceptedFilename) {
        try {
          await saveAcceptedFilename(currentReviewFile.fileId, acceptedFilename.trim());

          // Update the current review file with the new accepted name for the accept modal
          setCurrentReviewFile(prev => prev ? ({
            ...prev,
            acceptedName: acceptedFilename.trim(),
            newName: acceptedFilename.trim()
          }) : null);
        } catch (error) {
          console.error('Failed to save accepted filename before accept:', error);
          alert('Error saving filename. Please try again.');
          return; // Don't proceed with accept if filename save failed
        }
      }

      if (currentReviewFile.fileId && summaryFileAttributes && selectedClient) {
        try {
          const docType = summaryFileAttributes.doc_type || 'Mixed';
          const subType = summaryFileAttributes.sub_type || 'Mixed';
          const accountNumber = summaryFileAttributes.account_number || 'NONE';

          console.log('Saving metadata before accept:', { docType, subType, accountNumber });

          const metadataSaveResult = await sharepointService.saveFileMetadata(
            currentReviewFile.fileId,
            selectedClient,
            undefined, // editedSummary
            docType,
            subType,
            accountNumber
          );

          if (!metadataSaveResult.success) {
            console.warn('Failed to save metadata before accept:', metadataSaveResult.error);
            // Continue with accept even if metadata save fails, but warn user
            if (!window.confirm('Failed to save file metadata. Do you want to continue with accepting the file?')) {
              return;
            }
          } else {
            console.log('Metadata saved successfully before accept');

            // Update the local file list with the saved metadata
            setFiles(prev => prev.map(f =>
              f.fileId === currentReviewFile.fileId
                ? { ...f, doc_type: docType, sub_type: subType, account_number: accountNumber }
                : f
            ));
          }
        } catch (error) {
          console.error('Error saving metadata before accept:', error);
          // Continue with accept even if metadata save fails, but warn user
          if (!window.confirm('Error saving file metadata. Do you want to continue with accepting the file?')) {
            return;
          }
        }
      }

      setUploadOriginal(version === 'original');
      setIsApprovingAll(false);
      setAcceptingFile(currentReviewFile);
      setIsAcceptingFromSummary(true);
      setAcceptModalOpen(true);
    }
  };


  async function saveFileName(newName) {
    if (!editingFile || !selectedClient) return;

    try {
      await sharepointService.updateFileName(editingFile.fileId, newName, selectedClient);

      // Update the local state
      setFiles((prev) => prev.map((f) => f === editingFile ? { ...f, newName } : f));

      // Refresh the files list to get updated data from backend
      await loadFiles();
    } catch (error) {
      console.error('Error updating file name:', error);
      throw error; // Re-throw to let the modal handle the error display
    }
  }

  // Function to save accepted filename from the review modal
  async function saveAcceptedFilename(fileId, acceptedName) {
    if (!fileId || !acceptedName || !selectedClient) return;

    // Validate filename for knowledgebase compatibility
    const validation = validateFilename(acceptedName);
    if (!validation.isValid) {
      setFilenameSaveError(validation.error);
      throw new Error(validation.error);
    }

    try {
      setFilenameSaveError(''); // Clear any previous errors
      await sharepointService.updateFileName(fileId, acceptedName, selectedClient);
      console.log('Accepted filename saved successfully:', acceptedName);

      // Update the local state to reflect the change
      setFiles((prev) => prev.map((f) =>
        f.fileId === fileId ? {
          ...f,
          acceptedName: acceptedName,
          newName: acceptedName
        } : f
      ));
    } catch (error) {
      console.error('Error saving accepted filename:', error);
      setFilenameSaveError('Failed to save filename. Please try again.');
      throw error;
    }
  }

  // Helper functions for file preview (similar to ClientHelper and VectorInspector)
  function getFilenameFromUri(uri) {
    if (!uri) return '';
    const parts = uri.split('/');
    return decodeURIComponent(parts[parts.length - 1] || '');
  }

  function getS3KeyFromUri(uri) {
    if (!uri) return '';
    try {
      const parsed = new URL(uri);
      return decodeURIComponent((parsed.pathname || '').replace(/^\/+/, ''));
    } catch (error) {
      return '';
    }
  }

  // Fetch file content using sharepointService (similar to clientHelperService)
  const fetchFileContent = useCallback(async (fileUri) => {
    const filename = getFilenameFromUri(fileUri);
    const s3Key = getS3KeyFromUri(fileUri);
    let client_id = selectedClient;

    // When available, use the exact S3 key prefix as a robust fallback client identifier.
    // This helps older backend paths that still reconstruct keys from client_id.
    if (s3Key) {
      const keyPrefix = s3Key.split('/')[0];
      if (keyPrefix) {
        client_id = keyPrefix;
      }
    }

    try {
      // Check if it's a DOCX file and convert to PDF for preview
      const isDocx = filename.toLowerCase().endsWith('.docx');
      const convertToPdf = isDocx;

      const result = await sharepointService.downloadFile(
        filename,
        client_id,
        convertToPdf,
        s3Key || null
      );

      // SharePoint always returns a presigned URL now
      // The result contains { blob, headers }, and we need to fetch the actual presigned URL
      // Actually, we should modify this to get the presigned URL directly from the backend
      // For now, check if we have a blob and handle accordingly

      const blob = result.blob;
      const headers = result.headers || {};
      const contentType = blob?.type || headers['content-type'] || '';

      console.log('File download result:', { filename, contentType, blobSize: blob?.size, convertToPdf });

      // Check if blob is valid and has content
      if (!blob || blob.size === 0) {
        throw new Error('File not found or empty');
      }

      // Determine file type from filename extension
      const fileType = getFileType(filename);

      // For text files, read the blob content directly instead of creating a blob URL
      // Check both content type and file extension to handle cases where content-type is generic
      if (contentType.startsWith('text/') || contentType.includes('json') ||
        fileType === 'text' || filename.toLowerCase().endsWith('.json')) {
        return await blob.text();
      } else if (contentType.startsWith('image/') || fileType === 'image') {
        return URL.createObjectURL(blob);
      } else if (contentType === 'application/pdf' || filename.toLowerCase().endsWith('.pdf') || convertToPdf) {
        // Create blob URL for PDF with explicit type
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        console.log('Created PDF blob URL:', url);
        return url;
      } else if (contentType.includes('msword') || contentType.includes('officedocument') || fileType === 'doc') {
        return URL.createObjectURL(blob);
      } else {
        return URL.createObjectURL(blob);
      }
    } catch (error) {
      console.error('Error in fetchFileContent:', error);
      throw error;
    }
  }, [selectedClient]); // Only depend on selectedClient

  // Cleanup function to revoke blob URLs
  const revokeContentUrl = (content) => {
    if (typeof content === 'string' && content.startsWith('blob:')) {
      URL.revokeObjectURL(content);
    }
  };

  function handleFileClick(uri, filename) {
    if (!uri) return;
    setFileModalUri(uri);
    setFileModalFilename(filename || getFilenameFromUri(uri));
    setFileModalOpen(true);
  }

  // Summary modal functions
  const openSummaryModal = async (item) => {
    if (!item) return;

    let reviewFile = item;
    try {
      if (selectedClient && item.fileId) {
        const latestFiles = await sharepointService.listUploads(selectedClient);
        const latestMatch = (latestFiles || []).find((f) => f.fileId === item.fileId);
        if (latestMatch) {
          reviewFile = {
            ...item,
            ...latestMatch,
            file: item.file,
          };
        }
      }
    } catch (refreshError) {
      console.warn('Could not refresh latest SharePoint metadata before opening review modal:', refreshError);
    }

    // Set the current file being reviewed
    setPreviewVersion('redacted');
    setCurrentReviewFile(reviewFile);

    // Initialize modal state and show immediately
    setSummaryFileKey(reviewFile.fileId || '');
    setSummaryFilename(reviewFile.s3Key || 'Unknown');

    // Set suggested filename (read-only, from AI analysis)
    setSuggestedFilename(reviewFile.suggestedName || '');

    // Set accepted filename (editable, either existing accepted name or suggested as fallback)
    const initialAcceptedName = reviewFile.acceptedName || reviewFile.suggestedName || reviewFile.file?.name || '';
    setAcceptedFilename(initialAcceptedName);
    setOriginalAcceptedFilename(initialAcceptedName); // Store original to detect changes

    setFilenameSaveError(''); // Clear any previous errors
    setSummaryContent(null);
    setSummaryError(null);
    setDocumentSummary(reviewFile.documentSummary || '');
    setInlineRedactionJobData(null);
    setInlineRedactionLoading(false);
    setInlineRedactionError('');
    setSummaryContentView('summary');
    setSummaryModalOpen(true);

    // Check if we need to trigger analysis for suggested filename
    // Skip analysis for files already past filename approval (e.g. FPR/Cashflow forms published from client forms pages)
    const needsAnalysis = (!reviewFile.suggestedName || reviewFile.suggestedName.trim() === '') && ![
      STATUS_FILENAME_APPROVED,
      STATUS_PUSHING_TO_SHAREPOINT,
      STATUS_PUSHED,
      STATUS_PUSHED_TO_SHAREPOINT_DEPRECATED,
      STATUS_SUMMARY_APPROVED,
      STATUS_DONE,
    ].includes(reviewFile.status);
    if (needsAnalysis) {
      setSuggestedFilenameLoading(true);
    } else {
      setSuggestedFilenameLoading(false);
    }

    // Create initial file attributes structure using metadata from the file list
    const attrs = {
      doc_summary: reviewFile.documentSummary || '',
      doc_type: reviewFile.doc_type || reviewFile.docType || '',
      sub_type: reviewFile.sub_type || reviewFile.subType || '',
      account_number: reviewFile.account_number || reviewFile.accountNumber || '',
    };
    setSummaryFileAttributes(attrs);

    // Start all loading operations in parallel, but don't wait for them

    // 1. Load file content for preview
    const loadFileContent = async () => {
      setSummaryContentLoading(true);
      try {
        const content = await fetchFileContent(item.url);
        setSummaryContent(content);
      } catch (err) {
        console.error('Failed to fetch file content for summary', err);
        setSummaryError(err.message || 'Failed to load file content');
      } finally {
        setSummaryContentLoading(false);
      }
    };

    // 2. Trigger analysis if needed for suggested filename
    const triggerAnalysisIfNeeded = async () => {
      if (needsAnalysis && item.fileId) {
        try {
          console.log('Triggering analysis for file without suggested name:', item.fileId);

          // Update the file status in local state to show it's analyzing
          setFiles(prev => prev.map(f =>
            f === item ? { ...f, status: 'analyzing' } : f
          ));

          // Start async workflow
          const res = await sharepointService.analyzeFilesByIds([item.fileId], selectedClient);

          if (res.success && res.results && res.results.length > 0) {
            const jobId = res.results[0].jobId; // job_id is in jobId field

            if (jobId) {
              // Poll for workflow completion
              const pollInterval = 2000; // Poll every 2 seconds
              const maxAttempts = 150; // Max 5 minutes (150 * 2 seconds)

              for (let attempt = 0; attempt < maxAttempts; attempt++) {
                await new Promise(resolve => setTimeout(resolve, pollInterval));

                try {
                  const statusResp = await sharepointService.getWorkflowStatus(jobId);

                  const workflowStatus = statusResp.status;

                  if (workflowStatus === 'completed') {
                    // Workflow completed successfully
                    const newSuggestedName = statusResp.suggested_filename || '';
                    const newCategory = statusResp.category || '';
                    const newSummary = statusResp.summary || '';

                    // Update the suggested filename in the modal
                    setSuggestedFilename(newSuggestedName);

                    // Update accepted filename only if it wasn't already set
                    if (!item.acceptedName) {
                      setAcceptedFilename(newSuggestedName);
                      setOriginalAcceptedFilename(newSuggestedName);
                    }

                    // Update the file in local state
                    setFiles(prev => prev.map(f =>
                      f === item ? {
                        ...f,
                        suggestedName: newSuggestedName,
                        category: newCategory,
                        documentSummary: newSummary,
                        // Only update acceptedName and newName if there was no accepted name before
                        ...(f.acceptedName ? {} : { acceptedName: newSuggestedName, newName: newSuggestedName }),
                        status: 'analyzed'
                      } : f
                    ));

                    // Update the current review file
                    setCurrentReviewFile(prev => prev ? ({
                      ...prev,
                      suggestedName: newSuggestedName,
                      category: newCategory,
                      documentSummary: newSummary,
                      // Only update acceptedName and newName if there was no accepted name before
                      ...(prev.acceptedName ? {} : { acceptedName: newSuggestedName, newName: newSuggestedName }),
                      status: 'analyzed'
                    }) : null);

                    console.log('Analysis workflow completed:', newSuggestedName);
                    break;
                  } else if (workflowStatus === 'failed') {
                    console.error('Analysis workflow failed:', statusResp.error);
                    setFiles(prev => prev.map(f =>
                      f === item ? { ...f, status: 'analysis_error' } : f
                    ));
                    break;
                  }
                  // Continue polling for other statuses (pending, analyzing, etc.)
                } catch (pollError) {
                  console.error('Error polling workflow status:', pollError);
                  // Continue polling on error
                }
              }
            } else {
              console.warn('No job ID returned from analysis');
              setFiles(prev => prev.map(f =>
                f === item ? { ...f, status: 'analysis_error' } : f
              ));
            }
          } else {
            console.warn('Analysis failed or returned no results');
            setFiles(prev => prev.map(f =>
              f === item ? { ...f, status: 'analysis_error' } : f
            ));
          }
        } catch (error) {
          console.error('Error triggering analysis:', error);
          setFiles(prev => prev.map(f =>
            f === item ? { ...f, status: 'analysis_error' } : f
          ));
        } finally {
          setSuggestedFilenameLoading(false);
        }
      }
    };

    // 3. Load document summary if already available in metadata (don't auto-generate)
    const loadDocumentSummary = () => {
      // Only show existing document summary if available
      // Don't auto-generate - user must click "Regenerate Summary" or "Generate Custom Summary"
      if (reviewFile.documentSummary && reviewFile.documentSummary.trim()) {
        setOriginalDocumentSummary(reviewFile.documentSummary); // Store original summary

        // Check if there's an edited summary
        const edited = reviewFile.editedDocumentSummary || '';
        setEditedDocumentSummary(edited);

        // Display original by default
        setDocumentSummary(reviewFile.documentSummary);
        setCustomDocumentSummary(''); // No custom summary yet
        setIsCustomSummary(false);

        // Set view mode based on what's available
        if (edited) {
          setSummaryViewMode('edited');
          setDocumentSummary(edited);
        } else {
          setSummaryViewMode('original');
        }

        // Update local attributes with the stored summary
        const updatedAttrs = { ...attrs, doc_summary: reviewFile.documentSummary };
        setSummaryFileAttributes(prev => ({ ...prev, ...updatedAttrs }));
      } else {
        // No stored summary - leave empty, user can click button to generate
        setOriginalDocumentSummary('');
        setEditedDocumentSummary('');
        setCustomDocumentSummary('');
        setDocumentSummary('');
        setIsCustomSummary(false);
        setSummaryViewMode('original');
      }
    };

    // Start all operations in parallel without blocking the UI
    loadFileContent().catch(err => console.error('File content loading failed:', err));
    triggerAnalysisIfNeeded().catch(err => console.error('Analysis trigger failed:', err));
    loadDocumentSummary(); // This is now synchronous, no async needed
  };

  const loadInlineRedactionJob = useCallback(async (jobId) => {
    if (!jobId) {
      setInlineRedactionJobData(null);
      setInlineRedactionLoading(false);
      setInlineRedactionError('');
      return;
    }
    setInlineRedactionLoading(true);
    setInlineRedactionError('');
    try {
      const response = await redactorService.getJobStatus(jobId);
      setInlineRedactionJobData(response);
    } catch (err) {
      console.error('Failed to load inline redaction job', err);
      setInlineRedactionError(err.response?.data?.detail || err.message || 'Failed to load redaction review data.');
      setInlineRedactionJobData(null);
    } finally {
      setInlineRedactionLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!summaryModalOpen) return;
    const jobId = currentReviewFile?.redactionJobId;
    if (!jobId) {
      setInlineRedactionJobData(null);
      setInlineRedactionLoading(false);
      setInlineRedactionError('');
      return;
    }
    loadInlineRedactionJob(jobId);
  }, [summaryModalOpen, currentReviewFile?.redactionJobId, loadInlineRedactionJob]);

  const closeSummaryModal = async () => {
    // Save accepted filename if it has changed
    if (currentReviewFile && currentReviewFile.fileId &&
      acceptedFilename && acceptedFilename.trim() &&
      acceptedFilename !== originalAcceptedFilename) {
      try {
        await saveAcceptedFilename(currentReviewFile.fileId, acceptedFilename.trim());
      } catch (error) {
        console.error('Failed to save accepted filename on modal close:', error);
        // Show error to user but allow modal to close
        if (window.confirm('Failed to save filename changes. Do you want to close anyway? Your changes will be lost.')) {
          // User confirmed to close anyway
        } else {
          return; // Don't close modal, let user try again
        }
      }
    }

    // Save metadata if they have been modified
    if (currentReviewFile && currentReviewFile.fileId && summaryFileAttributes && selectedClient) {
      try {
        const docType = summaryFileAttributes.doc_type || 'Mixed';
        const subType = summaryFileAttributes.sub_type || 'Mixed';
        const accountNumber = summaryFileAttributes.account_number || 'NONE';

        console.log('Saving metadata on modal close:', { docType, subType, accountNumber, editedSummary: editedDocumentSummary });

        const metadataSaveResult = await sharepointService.saveFileMetadata(
          currentReviewFile.fileId,
          selectedClient,
          editedDocumentSummary, // Include edited summary
          docType,
          subType,
          accountNumber
        );

        if (!metadataSaveResult.success) {
          console.warn('Failed to save metadata:', metadataSaveResult.error);
          // Don't block modal closing for metadata save errors, just log them
        } else {
          console.log('Metadata saved successfully');

          // Update the local file list with the saved metadata
          setFiles(prev => prev.map(f =>
            f.fileId === currentReviewFile.fileId
              ? { ...f, editedDocumentSummary, doc_type: docType, sub_type: subType, account_number: accountNumber }
              : f
          ));
        }
      } catch (error) {
        console.error('Error saving metadata on modal close:', error);
        // Don't block modal closing for metadata save errors
      }
    }

    // Clean up blob URLs to prevent memory leaks
    if (summaryContent) {
      revokeContentUrl(summaryContent);
    }

    setSummaryModalOpen(false);
    setSummaryFileKey(null);
    setSummaryFilename(null);
    setSuggestedFilename('');
    setAcceptedFilename('');
    setOriginalAcceptedFilename('');
    setSuggestedFilenameLoading(false);
    setSummaryFileAttributes(null);
    setSummaryContent(null);
    setSummaryError(null);
    setSummaryContentLoading(false);
    setDocumentSummary('');
    setOriginalDocumentSummary(''); // Clear original summary
    setEditedDocumentSummary(''); // Clear edited summary
    setCustomDocumentSummary(''); // Clear custom summary
    setDocumentSummaryLoading(false);
    setIsCustomSummary(false);
    setSummaryViewMode('original'); // Reset view mode
    setFilenameSaveError(''); // Clear filename save errors
    setInlineRedactionJobData(null);
    setInlineRedactionLoading(false);
    setInlineRedactionError('');
    setCurrentReviewFile(null); // Clear the current review file
  };

  const handleRegenerateDocumentSummary = async () => {
    if (!summaryFileKey || !selectedClient) return;

    setDocumentSummaryLoading(true);
    try {
      // Start the async job (no custom prompts)
      const jobResp = await sharepointService.getDocumentSummary(
        summaryFileKey,
        selectedClient,
        null,
        null
      );

      if (jobResp && jobResp.success && jobResp.jobId) {
        const jobId = jobResp.jobId;

        // Poll for job completion
        const pollInterval = 2000; // Poll every 2 seconds
        const maxAttempts = 150; // Max 5 minutes

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          await new Promise(resolve => setTimeout(resolve, pollInterval));

          try {
            const statusResp = await sharepointService.getDocumentSummaryJobStatus(jobId);

            if (statusResp.status === 'completed') {
              // Job completed successfully
              const newSummary = statusResp.summary || '';
              const newSuggestedFilename = statusResp.suggested_filename || '';

              // Store as the new original and set to display it
              setOriginalDocumentSummary(newSummary);
              setDocumentSummary(newSummary);
              setIsCustomSummary(false);
              setSummaryViewMode('original');

              // Update suggested filename if provided
              if (newSuggestedFilename) {
                setSuggestedFilename(newSuggestedFilename);

                // Update accepted filename only if it wasn't already set by user
                if (!currentReviewFile?.acceptedName || currentReviewFile.acceptedName === currentReviewFile.suggestedName) {
                  setAcceptedFilename(newSuggestedFilename);
                  setOriginalAcceptedFilename(newSuggestedFilename);
                }

                // Update the current review file
                setCurrentReviewFile(prev => prev ? ({
                  ...prev,
                  suggestedName: newSuggestedFilename,
                  documentSummary: newSummary,
                  // Only update acceptedName if it wasn't already set by user
                  ...((!prev.acceptedName || prev.acceptedName === prev.suggestedName) ? {
                    acceptedName: newSuggestedFilename,
                    newName: newSuggestedFilename
                  } : {})
                }) : null);

                // Update the files list
                setFiles(prev => prev.map(f =>
                  f.fileId === currentReviewFile?.fileId ? {
                    ...f,
                    suggestedName: newSuggestedFilename,
                    documentSummary: newSummary,
                    // Only update acceptedName if it wasn't already set by user
                    ...((!f.acceptedName || f.acceptedName === f.suggestedName) ? {
                      acceptedName: newSuggestedFilename,
                      newName: newSuggestedFilename
                    } : {})
                  } : f
                ));
              }

              // Update local attributes
              const updatedAttrs = { ...summaryFileAttributes, doc_summary: newSummary };
              setSummaryFileAttributes(updatedAttrs);
              setDocumentSummaryLoading(false);
              return;
            } else if (statusResp.status === 'failed') {
              console.error('Document summary job failed:', statusResp.error);
              alert('Failed to regenerate document summary: ' + (statusResp.error || 'Unknown error'));
              setDocumentSummaryLoading(false);
              return;
            }
            // Continue polling for other statuses
          } catch (pollError) {
            console.error('Error polling summary job status:', pollError);
            // Continue polling on error
          }
        }

        // Timeout reached
        console.warn('Document summary job timed out');
        alert('Document summary generation timed out. Please try again.');
      } else {
        console.warn('Failed to start document summary job:', jobResp?.error);
        alert('Failed to regenerate document summary. Please try again.');
      }
    } catch (err) {
      console.error('Failed to regenerate document summary', err);
      alert('Error regenerating document summary. See console for details.');
    } finally {
      setDocumentSummaryLoading(false);
    }
  };

  const handleOpenCustomSummaryModal = () => {
    setCustomSummaryPrompt('');
    setSelectedClientPrompt('');
    setSelectedPromptSystemPrompt('');
    setSelectedPromptPrompt('');
    setSelectedPromptModelId('');
    setUseTextract(false);
    setCustomSummaryModalOpen(true);
  };

  const handleCloseCustomSummaryModal = () => {
    setCustomSummaryModalOpen(false);
    setCustomSummaryPrompt('');
    setSelectedClientPrompt('');
    setSelectedPromptSystemPrompt('');
    setSelectedPromptPrompt('');
    setSelectedPromptModelId('');
    setUseTextract(false);
  };

  const handleSubmitCustomSummary = async () => {
    if (!summaryFileKey || !selectedClient) return;

    let customSystemPrompt = null;
    let customUserPrompt = null;
    let customModelId = null;

    if (selectedClientPrompt) {
      // Use the selected client prompt's content
      if (!selectedPromptSystemPrompt.trim() && !selectedPromptPrompt.trim()) {
        alert('Please enter system prompt and/or prompt text.');
        return;
      }

      // Pass system and user prompts separately
      customSystemPrompt = selectedPromptSystemPrompt.trim() || null;
      customUserPrompt = selectedPromptPrompt.trim() || null;
      customModelId = selectedPromptModelId || null;
    } else {
      // Use the custom text area prompt as user prompt
      if (!customSummaryPrompt.trim()) {
        alert('Please enter a custom prompt.');
        return;
      }
      customUserPrompt = customSummaryPrompt.trim();
    }

    setDocumentSummaryLoading(true);
    setCustomSummaryModalOpen(false);

    try {
      // Start the async job with separate system and user prompts
      const jobResp = await sharepointService.getDocumentSummary(
        summaryFileKey,
        selectedClient,
        customSystemPrompt,
        customUserPrompt,
        useTextract,
        customModelId
      );

      if (jobResp && jobResp.success && jobResp.jobId) {
        const jobId = jobResp.jobId;

        // Poll for job completion
        const pollInterval = 2000; // Poll every 2 seconds
        const maxAttempts = 150; // Max 5 minutes

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          await new Promise(resolve => setTimeout(resolve, pollInterval));

          try {
            const statusResp = await sharepointService.getDocumentSummaryJobStatus(jobId);

            if (statusResp.status === 'completed') {
              // Job completed successfully
              const newSummary = statusResp.summary || '';

              // Store custom summary separately and display it
              setCustomDocumentSummary(newSummary);
              setDocumentSummary(newSummary);
              setIsCustomSummary(true);
              setSummaryViewMode('custom');
              setDocumentSummaryLoading(false);
              return;
            } else if (statusResp.status === 'failed') {
              console.error('Custom summary job failed:', statusResp.error);
              alert('Failed to generate custom document summary: ' + (statusResp.error || 'Unknown error'));
              setDocumentSummaryLoading(false);
              return;
            }
            // Continue polling for other statuses
          } catch (pollError) {
            console.error('Error polling custom summary job status:', pollError);
            // Continue polling on error
          }
        }

        // Timeout reached
        console.warn('Custom summary job timed out');
        alert('Custom summary generation timed out. Please try again.');
      } else {
        console.warn('Failed to start custom summary job:', jobResp?.error);
        alert('Failed to generate custom document summary. Please try again.');
      }
    } catch (err) {
      console.error('Failed to generate custom document summary', err);
      alert('Error generating custom document summary. See console for details.');
    } finally {
      setDocumentSummaryLoading(false);
    }
  };

  // Handler for exporting summary to PDF
  const handleExportSummaryToPdf = async () => {
    if (!summaryFileKey || !selectedClient) {
      alert('No file selected');
      return;
    }

    // Determine which summary to export based on current view mode
    let summaryToExport;
    if (summaryViewMode === 'custom' && customDocumentSummary) {
      summaryToExport = textToHtml(customDocumentSummary);
    } else if (summaryViewMode === 'edited' && editedDocumentSummary) {
      summaryToExport = editedDocumentSummary;
    } else if (summaryViewMode === 'original' && originalDocumentSummary) {
      summaryToExport = textToHtml(originalDocumentSummary);
    } else {
      // Fallback to current documentSummary
      summaryToExport = documentSummary ? textToHtml(documentSummary) : '';
    }

    if (!summaryToExport) {
      alert('No summary available to export');
      return;
    }

    try {
      // Show loading state
      const exportingToast = document.createElement('div');
      exportingToast.className = 'alert alert-info position-fixed';
      exportingToast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
      exportingToast.innerHTML = '<div class="spinner-border spinner-border-sm me-2" role="status"></div>Generating PDF...';
      document.body.appendChild(exportingToast);

      // Call the service to generate PDF
      const pdfBlob = await sharepointService.exportSummaryToPdf(
        summaryFileKey,
        summaryToExport,
        selectedClient
      );

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename based on original file name
      const originalFileName = currentReviewFile?.acceptedName || currentReviewFile?.s3Key || acceptedFilename || 'document';
      const baseFileName = originalFileName.split('/').pop().replace(/\.[^/.]+$/, '');
      link.download = `${baseFileName}_summary.pdf`;

      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      document.body.removeChild(exportingToast);

      // Show success message
      const successToast = document.createElement('div');
      successToast.className = 'alert alert-success position-fixed';
      successToast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
      successToast.innerHTML = '<i class="bi bi-check-circle me-2"></i>PDF downloaded successfully!';
      document.body.appendChild(successToast);
      setTimeout(() => {
        document.body.removeChild(successToast);
      }, 3000);

    } catch (error) {
      console.error('Error exporting summary to PDF:', error);
      alert('Failed to export summary to PDF. Please try again.');
    }
  };

  // Helper function to check if PDF can be displayed inline
  const canDisplayPdfInline = () => {
    // Check if browser supports PDF viewing
    const userAgent = navigator.userAgent.toLowerCase();
    const isChrome = userAgent.includes('chrome');
    const isFirefox = userAgent.includes('firefox');
    const isEdge = userAgent.includes('edge');
    const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome');

    // Most modern browsers support PDF viewing
    return isChrome || isFirefox || isEdge || isSafari;
  };

  // Helper function to get file type
  function getFileType(filename) {
    if (!filename) return "other";
    const ext = filename.split('.').pop().toLowerCase();
    if (["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"].includes(ext)) return "image";
    if (["txt", "csv", "log", "json", "md", "xml"].includes(ext)) return "text";
    if (["pdf"].includes(ext)) return "pdf";
    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext)) return "doc";
    return "other";
  }

  // Status helper functions are now imported from statusConstants.js

  // Get unique status values for filter dropdown
  function getUniqueStatuses() {
    const statuses = [...new Set(files.map(f => f.status))];
    return statuses.filter(status => status); // Remove undefined/null values
  }

  // Filter and sort files
  function getFilteredAndSortedFiles() {
    let filteredFiles = files;

    // Apply search filter
    if (searchFilter.trim()) {
      const searchTerm = searchFilter.toLowerCase().trim();
      filteredFiles = filteredFiles.filter(f =>
        (f.file?.name || '').toLowerCase().includes(searchTerm) ||
        (f.suggestedName || '').toLowerCase().includes(searchTerm) ||
        (f.acceptedName || '').toLowerCase().includes(searchTerm) ||
        (f.newName || '').toLowerCase().includes(searchTerm)
      );
    }

    // Apply status filter
    if (statusFilter) {
      filteredFiles = filteredFiles.filter(f => f.status === statusFilter);
    }

    // Apply sorting
    filteredFiles.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'original':
          aValue = (a.file?.name || '').toLowerCase();
          bValue = (b.file?.name || '').toLowerCase();
          break;
        case 'suggested':
          aValue = (a.suggestedName || '').toLowerCase();
          bValue = (b.suggestedName || '').toLowerCase();
          break;
        case 'accepted':
          // Use same logic as display: after approval, fall back to suggestedName if acceptedName not set
          aValue = (!canApproveFilename(a.status) ? (a.acceptedName || a.suggestedName || '') : (a.acceptedName || '')).toLowerCase();
          bValue = (!canApproveFilename(b.status) ? (b.acceptedName || b.suggestedName || '') : (b.acceptedName || '')).toLowerCase();
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'timestamp':
          aValue = new Date(a.timestamp || 0);
          bValue = new Date(b.timestamp || 0);
          break;
        default:
          aValue = (a.file?.name || '').toLowerCase();
          bValue = (b.file?.name || '').toLowerCase();
      }

      if (sortBy === 'timestamp') {
        // For dates, compare directly
        if (sortOrder === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      } else {
        // For strings, use localeCompare
        if (sortOrder === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
    });

    return filteredFiles;
  }

  // Toggle sort order or change sort field
  function handleSortChange(field) {
    if (sortBy === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Change field and reset to ascending
      setSortBy(field);
      setSortOrder('asc');
    }
  }

  // Clear all filters and reset sort
  function clearFiltersAndSort() {
    setSearchFilter('');
    setStatusFilter('');
    setSortBy('original');
    setSortOrder('asc');
  }

  // Toggle auto-refresh
  function toggleAutoRefresh() {
    setAutoRefreshEnabled(!autoRefreshEnabled);
  }

  // Handle fetching recent history
  async function handleFetchRecentHistory(excludeDone) {
    const data = await sharepointService.getRecentHistory(excludeDone);
    return data;
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Upload Client Documents to SharePoint</h3>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => setShowRecentHistoryDialog(true)}
          title="View recent upload history"
        >
          <i className="bi bi-clock-history me-2"></i>
          Recent History
        </button>
      </div>

      <div className="row mb-3 align-items-end">
        <div className="col-md-5">
          <label className="form-label">Select SharePoint Client</label>
          <div className="d-flex align-items-center">
            <SearchableDropdown
              options={clients}
              value={selectedClient}
              onChange={handleSharepointClientChange}
              placeholder="-- choose client --"
              searchPlaceholder="Search clients..."
              className="flex-grow-1 me-2"
            />
            <button
              type="button"
              className="btn btn-outline-success btn-sm"
              onClick={() => setShowCreateSharePointClientModal(true)}
              title="Create new SharePoint client"
            >
              <i className="bi bi-plus-lg"></i>
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm ms-1"
              onClick={() => setShowBackfillModal(true)}
              disabled={!selectedClient}
              title="Reorganize existing SharePoint files"
            >
              <i className="bi bi-arrow-repeat"></i>
            </button>
          </div>
        </div>
        <div className="col-md-2 text-center">
          <div className="text-muted fw-bold" style={{ paddingBottom: '8px' }}>
            Maps to →
          </div>
        </div>
        <div className="col-md-5">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="form-label mb-0">Select Knowledgebase Client</label>
            {selectedClient && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                style={{ fontSize: '0.8rem' }}
                onClick={() => {
                  setNewClientName(selectedClient.replace(/_REBELLIONAIRE/gi, '').trim());
                  setCreateModalAutoOpened(false);
                  setShowCreateClientModal(true);
                }}
              >
                + Create new client
              </button>
            )}
          </div>
          <select className="form-select" value={selectedKnowledgebaseClient} onChange={(e) => { setSelectedKnowledgebaseClient(e.target.value); setWeakMatchWarning(null); }}>
            <option value="">-- choose knowledgebase client --</option>
            {knowledgebaseClients.map((client) => (
              <option key={client.client_id} value={client.client_id}>
                {client.client_name}
              </option>
            ))}
          </select>
          {weakMatchWarning && (
            <div className="alert alert-warning py-2 px-3 mt-2 mb-0 d-flex align-items-center justify-content-between">
              <small>
                <i className="bi bi-exclamation-triangle me-1"></i>
                Weak match — only 1 word in common with <strong>{weakMatchWarning.client.client_name}</strong>. Is this correct?
              </small>
              <button
                type="button"
                className="btn btn-warning btn-sm ms-3 text-nowrap"
                onClick={() => {
                  setNewClientName(weakMatchWarning.sharepointName.replace(/_REBELLIONAIRE/gi, '').trim());
                  setCreateModalAutoOpened(false);
                  setShowCreateClientModal(true);
                }}
              >
                + Create new instead
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mb-3">
        <FileDrop onFiles={addFiles} disabled={!selectedClient} />
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-1">Files</h5>
            <button
              className="btn btn-sm"
              onClick={() => setShowStatusInfoModal(true)}
              title="Learn about file statuses"
              style={{
                fontSize: '16px',
                padding: '0.15rem 0.4rem',
                border: 'none',
                background: 'transparent',
                color: '#000'
              }}
            >
              <i className="bi bi-info-circle"></i>
            </button>
          </div>
          {lastRefreshTime && (
            <small className="text-muted">
              Last updated: {lastRefreshTime.toLocaleTimeString()}
              {autoRefreshEnabled && (
                <span className="ms-2">
                  <i className="bi bi-arrow-clockwise text-success"></i>
                  <span className="text-success">
                    {nextRefreshCountdown ? ` Next refresh in ${nextRefreshCountdown}s` : ' Auto-refreshing'}
                  </span>
                </span>
              )}
            </small>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="autoRefreshSwitch"
              checked={autoRefreshEnabled}
              onChange={toggleAutoRefresh}
              disabled={!selectedClient}
            />
            <label className="form-check-label small text-muted" htmlFor="autoRefreshSwitch">
              Auto-refresh (1 min)
            </label>
          </div>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={loadFiles}
            disabled={!selectedClient}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="row mb-3">
        <div className="col-md-4">
          <div className="input-group input-group-sm">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search files..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              disabled={!selectedClient}
            />
            {searchFilter && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setSearchFilter('')}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>
        <div className="col-md-2">
          <select
            className="form-select form-select-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            disabled={!selectedClient}
          >
            <option value="">All statuses</option>
            {getUniqueStatuses().map(status => (
              <option key={status} value={status}>
                {getStatusDisplayName(status)}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <div className="d-flex align-items-center gap-1 flex-nowrap justify-content-end">
            <span className="small text-muted text-nowrap me-1">Sort:</span>
            <button
              className={`btn btn-sm px-2 ${sortBy === 'original' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleSortChange('original')}
              disabled={!selectedClient}
              style={{ fontSize: '12px' }}
            >
              Original Name
              {sortBy === 'original' && (
                <i className={`bi bi-arrow-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
              )}
            </button>
            <button
              className={`btn btn-sm px-2 ${sortBy === 'suggested' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleSortChange('suggested')}
              disabled={!selectedClient}
              style={{ fontSize: '12px' }}
            >
              Suggested Name
              {sortBy === 'suggested' && (
                <i className={`bi bi-arrow-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
              )}
            </button>
            <button
              className={`btn btn-sm px-2 ${sortBy === 'accepted' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleSortChange('accepted')}
              disabled={!selectedClient}
              style={{ fontSize: '12px' }}
            >
              Accepted Name
              {sortBy === 'accepted' && (
                <i className={`bi bi-arrow-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
              )}
            </button>
            <button
              className={`btn btn-sm px-2 ${sortBy === 'status' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleSortChange('status')}
              disabled={!selectedClient}
              style={{ fontSize: '12px' }}
            >
              Status
              {sortBy === 'status' && (
                <i className={`bi bi-arrow-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
              )}
            </button>
            <button
              className={`btn btn-sm px-2 ${sortBy === 'timestamp' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleSortChange('timestamp')}
              disabled={!selectedClient}
              style={{ fontSize: '12px' }}
            >
              Date
              {sortBy === 'timestamp' && (
                <i className={`bi bi-arrow-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
              )}
            </button>
            <button
              className="btn btn-outline-secondary btn-sm px-2"
              onClick={clearFiltersAndSort}
              disabled={!selectedClient}
              title="Clear all filters and reset sort"
              style={{ fontSize: '12px' }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
      <div>
        {(() => {
          const filteredFiles = getFilteredAndSortedFiles();
          const eligibleVisibleFiles = filteredFiles.filter((f) => isBulkEligible(f));

          if (files.length === 0) {
            return <div className="text-muted">No files added</div>;
          }

          if (filteredFiles.length === 0) {
            return (
              <div className="text-muted text-center py-4">
                <i className="bi bi-search me-2"></i>
                No files match the current filters.
                <button
                  className="btn btn-link btn-sm p-0 ms-2"
                  onClick={clearFiltersAndSort}
                >
                  Clear filters
                </button>
              </div>
            );
          }

          return (
            <>
              <div className="small mb-2 d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  checked={eligibleVisibleFiles.length > 0 && eligibleVisibleFiles.every((f) => selectedFileIds.includes(f.fileId))}
                  onChange={(e) => toggleSelectAllVisible(eligibleVisibleFiles, e.target.checked)}
                  disabled={eligibleVisibleFiles.length === 0}
                />
                <span className="text-muted">Select all eligible files shown</span>

                <button
                  className="btn btn-outline-success btn-sm"
                  onClick={() => setShowBulkApproveModal(true)}
                  disabled={!selectedClient || selectedFileIds.length === 0 || !selectedKnowledgebaseClient}
                  title={!selectedKnowledgebaseClient ? 'Select a knowledgebase client first' : 'Bulk review and push selected files'}
                >
                  <i className="bi bi-lightning-charge me-1"></i>
                  Bulk Review & Push ({selectedFileIds.length})
                </button>

                <span className="text-muted ms-auto">
                  Showing {filteredFiles.length} of {files.length} files
                </span>
              </div>
              {filteredFiles.map((f, i) => (
                <div key={f.fileId || f.s3Key || `${f.file?.name}-${i}`} className="border rounded p-2 mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      {isBulkEligible(f) && (
                        <div className="form-check mb-1">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`bulk-select-${f.fileId}`}
                            checked={selectedFileIds.includes(f.fileId)}
                            onChange={() => toggleFileSelection(f.fileId)}
                          />
                          <label className="form-check-label small" htmlFor={`bulk-select-${f.fileId}`}>
                            Select for bulk push
                          </label>
                        </div>
                      )}
                      <div><strong>Original:</strong>
                        {f.url ? (
                          <button
                            type="button"
                            className="btn btn-link p-0 ms-1"
                            style={{ textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={() => handleFileClick(f.url, f.file.name)}
                          >
                            {f.file.name}
                          </button>
                        ) : (
                          <span className="ms-1">{f.file.name}</span>
                        )}
                      </div>
                      <div><strong>Suggested:</strong> {f.suggestedName || '-'}</div>
                      <div>
                        <strong>Accepted:</strong>{' '}
                        {(() => {
                          // If filename has been approved (status is filename_approved or higher),
                          // show the accepted name, falling back to suggested name if not explicitly set
                          if (!canApproveFilename(f.status)) {
                            return f.acceptedName || f.suggestedName || '-';
                          }
                          // Before approval, only show if explicitly set
                          return f.acceptedName || '-';
                        })()}
                      </div>
                      {f.category && <div><strong>Category:</strong> {f.category}</div>}
                      {f.timestamp && (
                        <div className="small text-muted">
                          <strong>Uploaded:</strong> {formatTimestamp(f.timestamp)}
                        </div>
                      )}
                    </div>
                    <div>
                      {/* Re-analyze button - show for error states and while analyzing */}
                      {(() => {
                        const isErrorState = f.status === STATUS_ANALYSIS_ERROR ||
                          f.status === STATUS_PUSH_ERROR ||
                          f.status === STATUS_DELETE_ERROR;
                        const isAnalyzing = f.status === STATUS_ANALYZING;

                        // Show Re-analyze button for error states or while analyzing
                        if (isErrorState || isAnalyzing) {
                          return (
                            <button
                              className="btn btn-primary btn-sm me-1"
                              onClick={() => handleAnalyze(f)}
                              disabled={isAnalyzing}
                            >
                              {isAnalyzing ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                  Analyzing...
                                </>
                              ) : (
                                'Re-analyze'
                              )}
                            </button>
                          );
                        }

                        // Don't show button for any other state (including done states)
                        return null;
                      })()}

                      {/* Review & Approve button - opens modal for all approval actions */}
                      <button
                        className={`btn btn-sm me-1 ${(f.status === STATUS_DONE || f.status === STATUS_SUMMARY_APPROVED) ? 'btn-success' : 'btn-warning'}`}
                        onClick={() => openSummaryModal(f)}
                        disabled={isProcessingStatus(f.status) && !f.documentSummary && !f.suggestedName}
                      >
                        {(() => {
                          // If done or summary approved, show "Review"
                          if (f.status === STATUS_DONE || f.status === STATUS_SUMMARY_APPROVED) {
                            return 'Review';
                          }
                          // If pushed to SharePoint but not done, show "Review & Approve"
                          if (f.status === STATUS_PUSHED || f.status === STATUS_PUSHED_TO_SHAREPOINT_DEPRECATED) {
                            return 'Review & Approve';
                          }
                          // Otherwise (not pushed yet), show "Review & Push"
                          return 'Review & Push';
                        })()}
                      </button>

                      {/* Delete button */}
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(f)}
                        disabled={f.status === STATUS_DELETING || !f.fileId}
                        title={!f.fileId ? 'Cannot delete: File ID missing' : `Delete ${f.acceptedName || f.suggestedName || f.file?.name || 'file'}`}
                      >
                        {f.status === STATUS_DELETING ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Deleting...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-trash3 me-1"></i>
                            Delete
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="mt-2">
                    Status: <span className={`badge ${getStatusColorClass(f.status)}`}>{f.statusDetail || getStatusDisplayName(f.status)}</span>
                  </div>
                  {f.sharepointLocation && <div className="mt-1">Sharepoint: {f.sharepointLocation}</div>}
                </div>
              ))}
            </>
          );
        })()}
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={fileModalOpen}
        onClose={() => setFileModalOpen(false)}
        fileUri={fileModalUri}
        filename={fileModalFilename}
        fetchFileContent={fetchFileContent}
      />

      {/* Edit File Name Modal */}
      <EditFileNameModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        currentName={editingFile?.newName || editingFile?.file?.name || ''}
        onSave={saveFileName}
      />

      {/* Create Knowledgebase Client Modal */}
      {showCreateClientModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Knowledgebase Client</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateClientModal(false);
                    if (createModalAutoOpened) setSelectedKnowledgebaseClient('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {createModalAutoOpened
                  ? <p>No matching knowledgebase client found for "<strong>{selectedClient}</strong>". Would you like to create a new one?</p>
                  : <p>Create a new knowledgebase client for "<strong>{selectedClient?.replace(/_REBELLIONAIRE/gi, '').trim()}</strong>".</p>
                }
                <div className="mb-3">
                  <label className="form-label">Client Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    placeholder="Enter client name"
                    disabled={creatingClient}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateClientModal(false);
                    if (createModalAutoOpened) setSelectedKnowledgebaseClient('');
                  }}
                  disabled={creatingClient}
                >
                  {createModalAutoOpened ? 'Skip' : 'Cancel'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateKnowledgebaseClient}
                  disabled={!newClientName.trim() || creatingClient}
                >
                  {creatingClient ? 'Creating...' : 'Create Client'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Modal (Document Preview) */}
      {summaryModalOpen && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog" style={{ maxWidth: '80%', width: '80%' }} role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  File Review - {summaryFilename ? getFilenameFromUri(summaryFilename) : 'N/A'}
                  {currentReviewFile && (
                    <span className={`badge ms-2 ${getStatusColorClass(currentReviewFile.status)}`}>
                      {isProcessingStatus(currentReviewFile.status) && (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      )}
                      {currentReviewFile.statusDetail || getStatusDisplayName(currentReviewFile.status)}
                    </span>
                  )}
                </h5>
                <button type="button" className="btn-close" onClick={closeSummaryModal}></button>
              </div>
              <div className="modal-body d-flex flex-column">
                <div className="row" style={{ height: 'calc(100vh - 225px)' }}>
                  {/* Left side - Document Preview */}
                  <div className="col-md-6 d-flex flex-column" style={{ height: '100%' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Document Preview</h6>
                      {!currentReviewFile?.redactionJobId && !summaryContentLoading && !summaryError && summaryContent && (() => {
                        const filename = getFilenameFromUri(summaryFilename);
                        const fileType = getFileType(filename);
                        return fileType === 'text' ? (
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="documentWordWrapToggle"
                              checked={wordWrap}
                              onChange={(e) => setWordWrap(e.target.checked)}
                            />
                            <label className="form-check-label small" htmlFor="documentWordWrapToggle">
                              Word wrap
                            </label>
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <div
                      className={`border rounded ${currentReviewFile?.redactionJobId ? 'p-2' : 'p-3'}`}
                      style={{
                        flex: '1',
                        overflow: currentReviewFile?.redactionJobId ? 'hidden' : 'auto',
                        minHeight: '0',
                        display: currentReviewFile?.redactionJobId ? 'flex' : 'block',
                        flexDirection: 'column',
                      }}
                    >
                      {currentReviewFile?.redactionJobId ? (
                        inlineRedactionLoading ? (
                          <div className="d-flex justify-content-center align-items-center h-100">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading redaction review...</span>
                            </div>
                          </div>
                        ) : inlineRedactionError ? (
                          <div className="d-flex align-items-start justify-content-start h-100 text-muted small">
                            Redaction review failed to load.
                          </div>
                        ) : (() => {
                          const allFiles = inlineRedactionJobData?.files || [];
                          const scopedFiles = currentReviewFile?.redactionFileId
                            ? allFiles.filter((file) => file.file_id === currentReviewFile.redactionFileId)
                            : allFiles;
                          if (!scopedFiles.length) {
                            return (
                              <div className="text-muted text-center d-flex align-items-center justify-content-center h-100">
                                Redaction job data was found, but no file payload is available yet.
                              </div>
                            );
                          }
                          return (
                            <>
                              <RedactorResultsViewer
                                key={currentReviewFile.fileId}
                                onPreviewVersionChange={setPreviewVersion}
                                jobId={currentReviewFile.redactionJobId}
                                files={scopedFiles}
                                redactionStatus={inlineRedactionJobData?.status || currentReviewFile.redactionStatus}
                                redactionError={currentReviewFile.redactionError || inlineRedactionError}
                                onRefresh={async () => {
                                  await loadInlineRedactionJob(currentReviewFile.redactionJobId);
                                  loadFiles();
                                }}
                              />
                            </>
                          );
                        })()
                      ) : summaryContentLoading ? (
                        <div className="d-flex justify-content-center align-items-center h-100">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading content...</span>
                          </div>
                        </div>
                      ) : summaryError ? (
                        <div className="text-danger text-center d-flex align-items-center justify-content-center h-100">
                          <div>
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            {summaryError}
                          </div>
                        </div>
                      ) : summaryContent ? (
                        (() => {
                          const filename = getFilenameFromUri(summaryFilename);
                          const fileType = getFileType(filename);

                          if (fileType === 'image') {
                            return <img src={summaryContent} alt={filename} style={{ maxWidth: '100%', height: 'auto' }} />;
                          } else if (fileType === 'text') {
                            const isHtml = typeof summaryContent === 'string' && /<html|<!DOCTYPE html/i.test(summaryContent);
                            if (isHtml) {
                              return <div className="text-danger">Unable to display file content (HTML received instead of text file).</div>;
                            }
                            return (
                              <pre
                                style={{
                                  overflow: 'auto',
                                  fontSize: '12px',
                                  whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
                                  wordBreak: wordWrap ? 'break-word' : 'normal'
                                }}
                              >
                                {summaryContent}
                              </pre>
                            );
                          } else if (fileType === 'pdf') {
                            const canDisplay = canDisplayPdfInline();
                            return (
                              <div style={{ height: '100%' }}>
                                {canDisplay ? (
                                  <iframe
                                    src={summaryContent}
                                    title={filename}
                                    style={{ width: '100%', height: '95%', border: 'none', overflow: 'hidden' }}
                                    onLoad={(e) => {
                                      console.log('Summary modal PDF iframe loaded successfully');
                                      // Additional check for iframe content
                                      try {
                                        const iframe = e.target;
                                        if (iframe.contentDocument || iframe.contentWindow) {
                                          console.log('PDF iframe has accessible content');
                                        }
                                      } catch (err) {
                                        console.log('PDF iframe content not accessible (expected for cross-origin)');
                                      }
                                    }}
                                    onError={(e) => {
                                      console.error('Summary modal PDF iframe error:', e);
                                    }}
                                  />
                                ) : (
                                  <div className="text-center" style={{ padding: '100px 20px' }}>
                                    <i className="bi bi-file-earmark-pdf" style={{ fontSize: '48px', color: '#dc3545' }}></i>
                                    <h5 className="mt-3">PDF Preview Not Available</h5>
                                    <p className="text-muted">Your browser doesn't support inline PDF viewing.</p>
                                  </div>
                                )}
                                <div className="mt-1 text-center">
                                  <a
                                    href={summaryContent}
                                    download={filename}
                                    className="btn btn-sm btn-outline-primary me-2"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <i className="bi bi-download me-1"></i>
                                    Download PDF
                                  </a>
                                  <a
                                    href={summaryContent}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-secondary"
                                  >
                                    <i className="bi bi-box-arrow-up-right me-1"></i>
                                    Open in New Tab
                                  </a>
                                </div>
                              </div>
                            );
                          } else if (fileType === 'doc') {
                            return <iframe src={summaryContent} title={filename} style={{ width: '100%', height: '99%', border: 'none', overflow: 'hidden' }} />;
                          } else {
                            return (
                              <div className="text-center" style={{ padding: '100px 0' }}>
                                <i className="bi bi-file-earmark me-2"></i>
                                <div>Preview not available for this file type</div>
                                <a href={summaryContent} download={filename} className="btn btn-primary mt-3">Download File</a>
                              </div>
                            );
                          }
                        })()
                      ) : (
                        <div className="text-muted text-center d-flex align-items-center justify-content-center h-100">
                          No content available
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right side */}
                  <div className="col-md-6 d-flex flex-column" style={{ height: '100%' }}>
                    <h6>File Attributes</h6>

                    <div className="mb-3">
                      <label className="form-label small mb-1"><strong>Suggested Filename (AI Generated):</strong></label>
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control form-control-sm bg-light"
                          value={suggestedFilename}
                          placeholder={suggestedFilenameLoading ? "Analyzing..." : "No suggested name yet"}
                          disabled={true}
                          readOnly={true}
                        />
                        {suggestedFilenameLoading && (
                          <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        )}
                      </div>
                      {suggestedFilenameLoading && (
                        <small className="text-muted">
                          <i className="bi bi-info-circle me-1"></i>
                          Analyzing file to generate suggested filename...
                        </small>
                      )}
                    </div>

                    <div className="mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <label className="form-label small mb-0 me-2"><strong>Accepted Filename:</strong></label>
                        {acceptedFilename && acceptedFilename !== originalAcceptedFilename && (
                          <span className="badge bg-warning text-dark small">
                            <i className="bi bi-pencil me-1"></i>Modified
                          </span>
                        )}
                      </div>
                      <input
                        type="text"
                        className={`form-control form-control-sm ${hasInvalidFilenameChars(acceptedFilename) ? 'is-invalid' : ''}`}
                        value={acceptedFilename}
                        onChange={(e) => {
                          setAcceptedFilename(e.target.value);
                          setFilenameSaveError(''); // Clear error when user starts typing
                        }}
                        placeholder="Enter accepted filename..."
                        disabled={suggestedFilenameLoading || (currentReviewFile && !canApproveFilename(currentReviewFile.status))}
                        readOnly={currentReviewFile && !canApproveFilename(currentReviewFile.status)}
                      />
                      {hasInvalidFilenameChars(acceptedFilename) && (
                        <div className="invalid-feedback">
                          Filename cannot contain brackets ([ or ]) or slashes (/ or \). These characters are not compatible with the knowledgebase.
                        </div>
                      )}
                      {currentReviewFile && !canApproveFilename(currentReviewFile.status) && (
                        <small className="text-muted">
                          <i className="bi bi-lock me-1"></i>
                          Filename is locked (already approved)
                        </small>
                      )}
                      {!suggestedFilenameLoading && acceptedFilename && acceptedFilename !== originalAcceptedFilename && currentReviewFile && canApproveFilename(currentReviewFile.status) && !hasInvalidFilenameChars(acceptedFilename) && (
                        <small className="text-success">
                          <i className="bi bi-check-circle me-1"></i>
                          Filename changes will be saved when you close this dialog or click Accept.
                        </small>
                      )}
                      {filenameSaveError && (
                        <small className="text-danger">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          {filenameSaveError}
                        </small>
                      )}
                    </div>

                    {/* Doc Type, Sub Type, and Account Number row */}
                    <div className="row mb-3">
                      <div className="col-md-4 ps-0">
                        <label className="form-label small mb-1"><strong>Doc Type:</strong></label>
                        <select
                          className="form-select form-select-sm"
                          value={(summaryFileAttributes && summaryFileAttributes.doc_type) || ''}
                          onChange={(e) => {
                            const selectedDocTypeId = e.target.value;
                            const currentSubTypeId = summaryFileAttributes?.sub_type || '';
                            // Get sub_types for the new doc_type (returns names)
                            const filteredSubNames = getFilteredSubTypes(selectedDocTypeId);
                            // Check if current sub_type name is still valid
                            const currentSubTypeName = getSubTypeName(currentSubTypeId);
                            const newSubType = filteredSubNames.includes(currentSubTypeName) ? currentSubTypeId : '';
                            setSummaryFileAttributes(prev => ({ ...(prev || {}), doc_type: selectedDocTypeId, sub_type: newSubType }));
                          }}
                        >
                          <option value="">Select doc type...</option>
                          {docTypesData.map((dt) => (<option key={dt.id} value={dt.id}>{dt.name}</option>))}
                        </select>
                      </div>

                      <div className="col-md-4">
                        <label className="form-label small mb-1"><strong>Sub Type:</strong></label>
                        <select
                          className="form-select form-select-sm"
                          value={(summaryFileAttributes && summaryFileAttributes.sub_type) || ''}
                          onChange={(e) => {
                            setSummaryFileAttributes(prev => ({ ...(prev || {}), sub_type: e.target.value }));
                          }}
                        >
                          <option value="">Select sub type...</option>
                          {(() => {
                            // Get the doc_type object to access sub_types with IDs
                            const currentDocTypeId = summaryFileAttributes?.doc_type || '';
                            const docTypeObj = docTypesData.find(dt => dt.id === currentDocTypeId);
                            if (docTypeObj && docTypeObj.sub_types && docTypeObj.sub_types.length > 0) {
                              return docTypeObj.sub_types.map(st => {
                                const id = typeof st === 'object' ? st.id : '';
                                const name = typeof st === 'object' ? st.name : st;
                                return <option key={id || name} value={id}>{name}</option>;
                              });
                            }
                            // No doc_type selected, show all sub_types (but we don't have IDs, so use names as values)
                            return subTypesList.map((opt) => (<option key={opt} value={opt}>{opt}</option>));
                          })()}
                        </select>
                      </div>

                      <div className="col-md-4 pe-0">
                        <label className="form-label small mb-1"><strong>Account Number:</strong></label>
                        <input
                          type="text"
                          className={`form-control form-control-sm ${summaryFileAttributes?.account_number &&
                            !/^(x\d{4}|Mixed|NONE|)$/i.test(summaryFileAttributes.account_number)
                            ? 'is-invalid'
                            : ''
                            }`}
                          placeholder="e.g., x1234, Mixed, or NONE"
                          value={(summaryFileAttributes && summaryFileAttributes.account_number) || ''}
                          onChange={(e) => {
                            setSummaryFileAttributes(prev => ({ ...(prev || {}), account_number: e.target.value }));
                          }}
                        />
                        {summaryFileAttributes?.account_number &&
                          !/^(x\d{4}|Mixed|NONE|)$/i.test(summaryFileAttributes.account_number) && (
                            <div className="invalid-feedback">
                              Format must be x followed by 4 digits (e.g., x1234), "Mixed", or "NONE"
                            </div>
                          )}
                      </div>
                    </div>

                    <div className="d-flex flex-column" style={{ flex: '1', minHeight: '0' }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                          <label className="form-label small mb-0 me-2">
                            <strong>{summaryContentView === 'data' && hasExtractionResult(currentReviewFile) ? 'Extracted Data Points:' : 'Document Summary:'}</strong>
                          </label>
                          <SummaryViewToggle
                            value={summaryContentView}
                            onChange={setSummaryContentView}
                            hasDataPoints={hasExtractionResult(currentReviewFile)}
                          />
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleRegenerateDocumentSummary}
                            disabled={documentSummaryLoading}
                          >
                            {documentSummaryLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Generating...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-arrow-clockwise me-2"></i>
                                Default Summary
                              </>
                            )}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleOpenCustomSummaryModal}
                            disabled={documentSummaryLoading}
                          >
                            <i className="bi bi-pencil-square me-2"></i>
                            Custom Summary
                          </button>
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={(e) => {
                              // Copy based on current view mode
                              let textToCopy;
                              if (summaryViewMode === 'custom' && customDocumentSummary) {
                                textToCopy = customDocumentSummary;
                              } else if (summaryViewMode === 'edited' && editedDocumentSummary) {
                                textToCopy = editedDocumentSummary;
                              } else if (summaryViewMode === 'original' && originalDocumentSummary) {
                                textToCopy = originalDocumentSummary;
                              } else {
                                textToCopy = documentSummary;
                              }

                              if (textToCopy) {
                                // Convert HTML to clean markdown/text, stripping annotations
                                const cleanText = htmlToMarkdown(textToCopy);
                                navigator.clipboard.writeText(cleanText).then(() => {
                                  // Show temporary success feedback
                                  const button = e.target.closest('button');
                                  const originalIcon = button.querySelector('i').className;
                                  button.querySelector('i').className = 'bi bi-check-lg';
                                  button.classList.remove('btn-outline-success');
                                  button.classList.add('btn-success');
                                  setTimeout(() => {
                                    button.querySelector('i').className = originalIcon;
                                    button.classList.remove('btn-success');
                                    button.classList.add('btn-outline-success');
                                  }, 1500);
                                }).catch(err => {
                                  console.error('Failed to copy to clipboard:', err);
                                  alert('Failed to copy to clipboard');
                                });
                              }
                            }}
                            disabled={documentSummaryLoading || (!editedDocumentSummary && !documentSummary && !customDocumentSummary)}
                            title="Copy summary to clipboard"
                          >
                            <i className="bi bi-clipboard"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleExportSummaryToPdf}
                            disabled={documentSummaryLoading || (!editedDocumentSummary && !documentSummary && !customDocumentSummary)}
                            title="Export summary to PDF"
                          >
                            <i className="bi bi-download"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => {
                              setTriggerSummaryEdit(true);
                              setTimeout(() => setTriggerSummaryEdit(false), 100);
                            }}
                            disabled={documentSummaryLoading}
                            title="Edit summary"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        </div>
                      </div>
                      <div style={{ flex: '1', minHeight: '0', overflow: 'auto' }}>
                        {summaryContentView === 'data' && hasExtractionResult(currentReviewFile) ? (
                          <DataPointsTable
                            dataPoints={currentReviewFile.docDataPoints}
                            details={currentReviewFile.docDataPointsDetails}
                          />
                        ) : documentSummaryLoading ? (
                          <div className="text-center text-muted border rounded p-3" style={{ padding: '40px 0', backgroundColor: '#f8f9fa' }}>
                            <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                            Generating summary...
                          </div>
                        ) : (
                          <SummaryEditor
                            value={documentSummary}
                            originalSummary={originalDocumentSummary}
                            editedSummary={editedDocumentSummary}
                            customSummary={customDocumentSummary}
                            onChange={(newValue) => {
                              // When editing, update the edited summary
                              setEditedDocumentSummary(newValue);
                              setDocumentSummary(newValue);
                              // Switch to edited view after saving
                              setSummaryViewMode('edited');
                              setIsCustomSummary(false);
                            }}
                            onViewChange={(viewMode, summary) => {
                              // Handle view changes from the toggle in SummaryEditor
                              setSummaryViewMode(viewMode);
                              setDocumentSummary(summary);
                              setIsCustomSummary(viewMode === 'custom');
                            }}
                            disabled={documentSummaryLoading}
                            externalEditTrigger={triggerSummaryEdit}
                            onEditModeChange={(viewState) => {
                              setSummaryViewState(viewState);
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeSummaryModal}>Close</button>

                {/* Approve All & Push button - show only if filename not yet approved */}
                {currentReviewFile && canApproveFilename(currentReviewFile.status) && (
                  <SharePointApprovalButton
                    previewVersion={previewVersion}
                    originalActionLabel="Approve All & Push (unredacted)"
                    onApprove={handleApproveAllFromModal}
                    disabled={isProcessingStatus(currentReviewFile.status) || isRedactionBlocking(currentReviewFile)}
                  >
                    {isProcessingStatus(currentReviewFile.status) ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-all me-2"></i>
                        Approve All & Push
                      </>
                    )}
                  </SharePointApprovalButton>
                )}

                {/* Push to SharePoint button - show only if filename approved but not yet pushed */}
                {currentReviewFile && canPushToSharePoint(currentReviewFile.status) && !canApproveFilename(currentReviewFile.status) && (
                  <SharePointApprovalButton
                    previewVersion={previewVersion}
                    originalActionLabel="Push to SharePoint (unredacted)"
                    onApprove={handleAcceptFromSummary}
                    disabled={isProcessingStatus(currentReviewFile.status) || isRedactionBlocking(currentReviewFile)}
                  >
                    {isProcessingStatus(currentReviewFile.status) ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Pushing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-cloud-upload me-2"></i>
                        Push to SharePoint
                      </>
                    )}
                  </SharePointApprovalButton>
                )}

                {/* Approve Summary button - show only if pushed but summary not approved */}
                {currentReviewFile && canApproveSummary(currentReviewFile.status) && (
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={() => handleApproveSummary(currentReviewFile)}
                    disabled={isProcessingStatus(currentReviewFile.status)}
                  >
                    <i className="bi bi-check-square me-2"></i>
                    Approve Summary
                  </button>
                )}

                {/* Relocate/Rename button - show if file is already in SharePoint */}
                {currentReviewFile && canRelocateFile(currentReviewFile) && (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleOpenRelocateModal}
                    disabled={isProcessingStatus(currentReviewFile.status)}
                    title="Rename or move this file to a different folder in SharePoint"
                  >
                    <i className="bi bi-arrow-left-right me-2"></i>
                    Rename / Move
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {summaryModalOpen && <div className="modal-backdrop fade show"></div>}

      {/* Custom Summary Modal */}
      {customSummaryModalOpen && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Custom Summary Prompt</h5>
                <button type="button" className="btn-close" onClick={handleCloseCustomSummaryModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Client Prompts:</label>
                  <select
                    className="form-select"
                    value={selectedClientPrompt}
                    onChange={(e) => {
                      const promptId = e.target.value;
                      setSelectedClientPrompt(promptId);

                      if (promptId) {
                        // Find the selected prompt and populate the fields
                        const selectedPrompt = clientTypePrompts.find(p => p.prompt_id === promptId);
                        if (selectedPrompt) {
                          setSelectedPromptSystemPrompt(selectedPrompt.system_prompt || '');
                          setSelectedPromptPrompt(selectedPrompt.prompt || '');
                          setSelectedPromptModelId(selectedPrompt.model_id || '');
                        }
                      } else {
                        // Reset fields when no prompt is selected
                        setSelectedPromptSystemPrompt('');
                        setSelectedPromptPrompt('');
                        setSelectedPromptModelId('');
                      }
                    }}
                    disabled={promptsLoading}
                  >
                    <option value="">-- Select a client type prompt or enter custom text below --</option>
                    {clientTypePrompts.map(prompt => (
                      <option key={prompt.prompt_id} value={prompt.prompt_id}>
                        {prompt.name}
                      </option>
                    ))}
                  </select>
                  {promptsLoading && (
                    <div className="text-muted small mt-1">
                      <i className="bi bi-hourglass-split me-1"></i>
                      Loading client type prompts...
                    </div>
                  )}
                </div>

                {selectedClientPrompt ? (
                  <>
                    {/* Show editable prompt fields when a client prompt is selected */}
                    <div className="mb-3">
                      <label className="form-label">System Prompt:</label>
                      <textarea
                        className="form-control"
                        rows={4}
                        value={selectedPromptSystemPrompt}
                        onChange={e => setSelectedPromptSystemPrompt(e.target.value)}
                        placeholder="Enter system prompt..."
                        style={{ fontSize: '14px', lineHeight: '1.4' }}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Prompt:</label>
                      <textarea
                        className="form-control"
                        rows={6}
                        value={selectedPromptPrompt}
                        onChange={e => setSelectedPromptPrompt(e.target.value)}
                        placeholder="Enter prompt text..."
                        style={{ fontSize: '14px', lineHeight: '1.4' }}
                      />
                    </div>
                    {/* Use Textract Toggle for custom prompts */}
                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="useTextractToggle"
                          checked={useTextract}
                          onChange={(e) => setUseTextract(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="useTextractToggle">
                          Use Textract OCR for PDF text extraction
                        </label>
                      </div>
                      <small className="text-muted ms-4">
                        <i className="bi bi-info-circle me-1"></i>
                        Enable this for PDFs with scanned images or complex formatting that may not work well with standard analysis.
                      </small>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Show custom text area when no client prompt is selected */}
                    <div className="mb-3">
                      <label className="form-label">Enter your custom prompt for the document summary:</label>
                      <textarea
                        className="form-control"
                        rows={8}
                        value={customSummaryPrompt}
                        onChange={e => setCustomSummaryPrompt(e.target.value)}
                        placeholder="Example: Provide a detailed financial analysis of this document, focusing on investment recommendations and risk assessments..."
                        style={{ fontSize: '14px', lineHeight: '1.4' }}
                      />
                    </div>
                    {/* Use Textract Toggle for custom prompts */}
                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="useTextractToggle"
                          checked={useTextract}
                          onChange={(e) => setUseTextract(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="useTextractToggle">
                          Use Textract OCR for PDF text extraction
                        </label>
                      </div>
                      <small className="text-muted ms-4">
                        <i className="bi bi-info-circle me-1"></i>
                        Enable this for PDFs with scanned images or complex formatting that may not work well with standard analysis.
                      </small>
                    </div>
                  </>
                )}
                <div className="text-muted small">
                  <i className="bi bi-exclamation-circle me-1"></i>
                  Your custom prompt and resulting summary will not be saved off. Please manually save any custom prompts or summaries you wish to keep.
                </div>
                <div className="text-muted small">
                  <i className="bi bi-exclamation-circle me-1"></i>
                  If your prompt has variables in italic (e.g., <em>{'{{client_name}}'}</em>), they will not be automatically replaced. Please include any necessary context directly in your prompt.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseCustomSummaryModal}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmitCustomSummary}
                  disabled={selectedClientPrompt ?
                    (!selectedPromptSystemPrompt.trim() && !selectedPromptPrompt.trim()) :
                    !customSummaryPrompt.trim()
                  }
                >
                  <i className="bi bi-magic me-2"></i>
                  Generate Custom Summary
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {customSummaryModalOpen && <div className="modal-backdrop fade show"></div>}

      {/* Accept File Modal */}
      <AcceptFileModal
        uploadOriginal={uploadOriginal}
        isOpen={acceptModalOpen}
        onClose={closeAcceptModal}
        file={acceptingFile}
        clientName={selectedClient}
        knowledgebaseClientId={selectedKnowledgebaseClient}
        onAccept={handleAcceptConfirm}
        isApprovingAll={isApprovingAll}
        suggestedFolder={acceptingFile?.suggestedFolder}
        folderConfidence={acceptingFile?.folderConfidence}
        docType={(summaryFileAttributes && summaryFileAttributes.doc_type) || getMixedDocTypeId()}
        subType={(summaryFileAttributes && summaryFileAttributes.sub_type) || getMixedSubTypeId()}
        accountNumber={(summaryFileAttributes && summaryFileAttributes.account_number) || 'NONE'}
        fileExistsInSharePoint={!!acceptingFile?.sharepointLocation}
      />

      <BulkApprovePushModal
        isOpen={showBulkApproveModal}
        onClose={() => {
          if (!bulkSubmitting) {
            setShowBulkApproveModal(false);
          }
        }}
        files={files.filter((f) => selectedFileIds.includes(f.fileId) && isBulkEligible(f))}
        docTypesData={docTypesData}
        allSubTypes={subTypesList}
        onPreview={(file) => handleFileClick(file?.url, file?.file?.name)}
        onSubmit={handleBulkApproveSubmit}
        isSubmitting={bulkSubmitting}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        fileName={fileToDelete?.newName || fileToDelete?.file?.name || 'Unknown file'}
        isDeleting={fileToDelete?.status === 'deleting'}
      />

      {/* Create SharePoint Client Modal */}
      {showCreateSharePointClientModal && (
        <CreateClientModal
          isOpen={showCreateSharePointClientModal}
          onClose={() => setShowCreateSharePointClientModal(false)}
          onClientCreated={handleSharePointClientCreated}
        />
      )}

      {/* Status Info Modal */}
      {showStatusInfoModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-info-circle me-2"></i>
                  File Status Workflow
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowStatusInfoModal(false)}></button>
              </div>
              <div className="modal-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                <p className="lead">Files progress through the following statuses:</p>

                <div className="mb-4">
                  <h6 className="fw-bold text-primary">
                    <span className="badge bg-secondary me-2">1</span>
                    Queued
                  </h6>
                  <p className="ms-4">File has been selected for upload but not yet sent to the server.</p>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold text-primary">
                    <span className="badge bg-secondary me-2">2</span>
                    Uploading
                  </h6>
                  <p className="ms-4">File is being uploaded to S3 storage.</p>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold text-primary">
                    <span className="badge bg-secondary me-2">3</span>
                    Uploaded
                  </h6>
                  <p className="ms-4">File has been successfully uploaded to S3 and is ready for analysis.</p>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold text-warning">
                    <span className="badge bg-secondary me-2">4</span>
                    Analyzing
                  </h6>
                  <p className="ms-4">
                    AI is analyzing the file to:
                    <ul className="mt-2">
                      <li>Generate a suggested filename</li>
                      <li>Determine the document category</li>
                      <li>Create a document summary</li>
                    </ul>
                  </p>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold text-info">
                    <span className="badge bg-secondary me-2">5</span>
                    Analyzed
                  </h6>
                  <p className="ms-4">
                    Analysis complete. Suggested filename, category, and summary are available for review.
                    <br />
                    <strong>Action:</strong> Review the file details and click "Review & Approve" to proceed.
                  </p>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold text-success">
                    <span className="badge bg-secondary me-2">6</span>
                    Filename Approved
                  </h6>
                  <p className="ms-4">
                    The filename has been approved and is locked. File is ready to be pushed to SharePoint.
                    <br />
                    <strong>Action:</strong> Select a SharePoint folder and click "Push to SharePoint".
                  </p>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold text-warning">
                    <span className="badge bg-secondary me-2">7</span>
                    Pushing to SharePoint
                  </h6>
                  <p className="ms-4">File is being uploaded to SharePoint and added to the knowledgebase.</p>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold text-success">
                    <span className="badge bg-secondary me-2">8</span>
                    Pushed
                  </h6>
                  <p className="ms-4">
                    File has been successfully pushed to SharePoint and added to the knowledgebase.
                    <br />
                    <strong>Action:</strong> Review the summary and click "Approve Summary" to complete.
                  </p>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold text-success">
                    <span className="badge bg-secondary me-2">9</span>
                    Summary Approved
                  </h6>
                  <p className="ms-4">Summary has been approved. File processing is nearly complete.</p>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold text-success">
                    <span className="badge bg-success me-2">✓</span>
                    Done
                  </h6>
                  <p className="ms-4">
                    File processing is complete! The file is in SharePoint, the knowledgebase, it will stay in this list for 180 day.
                  </p>
                </div>

                <hr className="my-4" />

                <div className="mb-3">
                  <h6 className="fw-bold text-danger">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Error States
                  </h6>
                  <ul>
                    <li>
                      <strong>Analysis Error:</strong> AI analysis failed. Click "Re-analyze" to retry.
                    </li>
                    <li>
                      <strong>Push Error:</strong> Upload to SharePoint failed. Click "Re-analyze" to retry.
                    </li>
                    <li>
                      <strong>Delete Error:</strong> File deletion failed. Try deleting again.
                    </li>
                  </ul>
                </div>

                <div className="alert alert-info">
                  <i className="bi bi-lightbulb me-2"></i>
                  <strong>Quick Actions:</strong>
                  <ul className="mb-0 mt-2">
                    <li><strong>Approve All & Push:</strong> Approves filename, pushes to SharePoint, and marks as done in one action</li>
                    <li><strong>Upload version:</strong> Approval uses the selected preview version. Use the Unredacted dropdown beside the push button to explicitly approve and push the original, regardless of the preview or whether redaction ran. Original uploads require confirmation. Bulk uploads keep the default redaction behavior.</li>
                    <li><strong>Approve Summary:</strong> Marks the file as done after reviewing the summary</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-primary" onClick={() => setShowStatusInfoModal(false)}>
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showStatusInfoModal && <div className="modal-backdrop fade show"></div>}

      {/* Recent History Dialog */}
      <RecentHistoryDialog
        isOpen={showRecentHistoryDialog}
        onClose={() => setShowRecentHistoryDialog(false)}
        onFetchHistory={handleFetchRecentHistory}
        onSelectClient={handleSharepointClientChange}
      />

      {/* Backfill Modal */}
      <BackfillModal
        isOpen={showBackfillModal}
        onClose={() => setShowBackfillModal(false)}
        clientName={selectedClient}
        knowledgebaseClientId={selectedKnowledgebaseClient}
        onComplete={(result) => {
          console.log('Backfill complete:', result);
          // Refresh the files list after backfill
          loadFiles();
        }}
      />

      {/* Relocate File Modal */}
      <RelocateFileModal
        isOpen={showRelocateModal}
        onClose={() => setShowRelocateModal(false)}
        file={currentReviewFile}
        clientName={selectedClient}
        knowledgebaseClientId={selectedKnowledgebaseClient}
        onRelocate={handleFileRelocated}
      />
    </div>
  );
}
